import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { PRIORITIES, PRIORITY_LABEL } from "../lib/labels";
import { toast } from "sonner";
import { EnvelopeOpen, FloppyDisk, Clock, PlugsConnected, Signature, MicrosoftOutlookLogo, CheckCircle, XCircle, Link as LinkIcon, BellRinging, Phone, Star, Plus, TrashSimple, ArrowUp, ArrowDown, Megaphone, Warning, ArrowsClockwise, EnvelopeSimple, Monitor, Copy, Robot, RocketLaunch, Bug, BookOpen, Info, MapPin, Globe, Buildings, ChartBar } from "@phosphor-icons/react";
import Escalations from "./Escalations";
import RichEditor from "../components/RichEditor";
import RichView from "../components/RichView";
import PushSetup from "../components/PushSetup";
import BugReports from "./BugReports";
import Documentation from "./Documentation";
import AboutContent from "../components/AboutContent";
import WhatsAppDiagnosticBanner from "../components/WhatsAppDiagnosticBanner";

const PROVIDERS = [
  { v: "imap", label: "IMAP standard (Gmail, OVH, IONOS…)" },
  { v: "graph_app", label: "Microsoft 365 — Application (client_credentials)" },
  { v: "graph_delegated", label: "Microsoft 365 — Délégué (utilisateur consent)" },
];


// Default HTML for each customizable email template — mirrors the fallback
// content used server-side when the corresponding PortalSettings field is empty.
// Provided as a one-click "Charger le modèle par défaut" seed for admins.
const DEFAULT_TEMPLATES = {
  ticket_created_message: `<p>Bonjour <b>{client_name}</b>,</p>
<p>Nous avons bien reçu votre demande d'assistance.<br/>
Votre ticket <b>#{ticket_number}</b> est enregistré et sera pris en charge dans les meilleurs délais.</p>
<table style="width:100%;border-collapse:collapse;margin-top:16px;font-size:13px;">
  <tr><td style="padding:6px 0;color:#64748b;">Sujet</td><td style="padding:6px 0;"><b>{ticket_subject}</b></td></tr>
  <tr><td style="padding:6px 0;color:#64748b;">Priorité</td><td style="padding:6px 0;text-transform:capitalize;">{priority}</td></tr>
  <tr><td style="padding:6px 0;color:#64748b;">Catégorie</td><td style="padding:6px 0;text-transform:capitalize;">{category}</td></tr>
</table>`,
  taken_over_message: `<p>Bonjour <b>{client_name}</b>,</p>
<p>Bonne nouvelle : votre ticket <b>#{ticket_number}</b> est désormais pris en charge par <b>{tech_name}</b>.</p>
<table style="width:100%;border-collapse:collapse;margin-top:16px;font-size:13px;">
  <tr><td style="padding:6px 0;color:#64748b;">Sujet</td><td style="padding:6px 0;"><b>{ticket_subject}</b></td></tr>
  <tr><td style="padding:6px 0;color:#64748b;">Statut</td><td style="padding:6px 0;">En cours de traitement</td></tr>
  <tr><td style="padding:6px 0;color:#64748b;">Contact direct</td><td style="padding:6px 0;">{tech_phone}</td></tr>
</table>
<p style="color:#64748b;font-size:13px;margin-top:14px;">Vous recevrez une notification à chaque avancement du dossier.</p>`,
  new_reply_message: `<p>Bonjour <b>{client_name}</b>,</p>
<p><b>{author_name}</b> vient de répondre à votre ticket <b>#{ticket_number}</b> :</p>
<blockquote style="margin:16px 0;padding:14px 18px;border-left:3px solid #1e40af;background:#f1f5f9;color:#334155;font-size:14px;">{comment_body}</blockquote>
<p style="color:#64748b;font-size:13px;">Répondez directement en ré-ouvrant votre ticket depuis votre espace ou en répondant à cet e-mail.</p>`,
  status_change_message: `<p>Bonjour <b>{client_name}</b>,</p>
<p>Le statut de votre ticket <b>#{ticket_number}</b> vient d'évoluer :</p>
<p style="margin:12px 0;padding:10px 14px;background:#f1f5f9;border-left:3px solid #1e40af;"><b>{old_status}</b> → <b>{new_status}</b></p>
<p>Ce ticket est pris en charge par <b>{tech_name}</b>. Vous serez informé(e) à chaque évolution.</p>`,
  auto_close_reminder_message: `<p>Bonjour <b>{client_name}</b>,</p>
<p>Sans nouvelle de votre part sous <b>{days_left}</b> jour(s), nous clôturerons automatiquement votre ticket <b>#{ticket_number}</b> — <i>{ticket_subject}</i>.</p>
<p>Si votre problème est toujours d'actualité, répondez simplement à cet email pour nous en informer.</p>`,
  auto_close_message: `<p>Bonjour <b>{client_name}</b>,</p>
<p>Ce ticket a été clôturé automatiquement car nous n'avons pas eu de nouvelle de votre part depuis <b>{days}</b> jour(s).</p>
<p>Ticket <b>#{ticket_number}</b> — <i>{ticket_subject}</i></p>
<p>Si votre problème persiste, n'hésitez pas à créer un nouveau ticket, nous serons ravis de vous aider.</p>`,
  contact_info_message: `<p>Bonjour <b>{client_name}</b>,</p>
<p>Dans le cadre de votre contrat de maintenance, plusieurs moyens sont à votre disposition pour nous joindre :</p>
<ul>
  <li>📧 <b>Email support</b> : support@netkom.fr</li>
  <li>🖥️ <b>Portail client</b> : ouvrez un ticket en ligne 24/7 pour un suivi complet</li>
  <li>☎️ <b>Téléphone</b> : notre équipe est joignable durant les heures ouvrées</li>
  <li>🤖 <b>Assistant vocal IA</b> : disponible en dehors des heures ouvrées pour prendre votre message</li>
</ul>
<p>N'hésitez pas à choisir le canal qui vous convient — notre équipe reste à votre écoute.</p>
<p style="color:#64748b;font-size:13px;margin-top:14px;">L'équipe {brand}</p>`,
  hosting_info_message: `<p>Bonjour <b>{client_name}</b>,</p>
<p>Voici le récapitulatif des informations d'hébergement de <b>{domain}</b>.</p>
<table style="width:100%;border-collapse:collapse;margin-top:16px;font-size:13px;">
  <tr><td style="padding:6px 0;color:#64748b;">Formule</td><td style="padding:6px 0;"><b>{plan}</b></td></tr>
  <tr><td style="padding:6px 0;color:#64748b;">Espace disque</td><td style="padding:6px 0;">{storage_gb} Go</td></tr>
  <tr><td style="padding:6px 0;color:#64748b;">Bande passante</td><td style="padding:6px 0;">{bandwidth_gb} Go</td></tr>
  <tr><td style="padding:6px 0;color:#64748b;">Renouvellement</td><td style="padding:6px 0;">{renewal_date}</td></tr>
  <tr><td style="padding:6px 0;color:#64748b;">Webmail</td><td style="padding:6px 0;"><a href="{webmail_url}">{webmail_url}</a></td></tr>
  <tr><td style="padding:6px 0;color:#64748b;">Panneau de contrôle</td><td style="padding:6px 0;"><a href="{control_panel_url}">{control_panel_url}</a></td></tr>
</table>
{credentials_table}
<p style="color:#64748b;font-size:13px;margin-top:14px;">Conservez précieusement ce récapitulatif. — L'équipe {brand}</p>`,
  hosting_renewal_message: `<p>Bonjour <b>{client_name}</b>,</p>
<p>Votre hébergement <b>{domain}</b> ({plan}) arrive à échéance le <b>{renewal_date}</b>, soit dans <b>{days_left} jour(s)</b>.</p>
<p>Pour éviter toute interruption de service (site indisponible, boîtes mail bloquées), merci de nous confirmer le renouvellement au plus tôt en répondant à cet email.</p>
<p style="color:#64748b;font-size:13px;margin-top:14px;">L'équipe {brand}</p>`,
  domain_renewal_message: `<p>Bonjour <b>{client_name}</b>,</p>
<p>Le nom de domaine <b>{domain}</b> expire le <b>{expires_at}</b>, soit dans <b>{days_left} jour(s)</b>.</p>
<p>Sans renouvellement, votre nom de domaine peut être libéré et récupéré par un tiers. Merci de nous confirmer si vous souhaitez le renouveler auprès de <b>{registrar}</b>.</p>
<p style="color:#64748b;font-size:13px;margin-top:14px;">L'équipe {brand}</p>`,
  license_low_stock_subject: `[{brand}] 🛒 Stock licences bas — {title} ({remaining}/{total})`,
  license_low_stock_message: `<p>Bonjour,</p>
<p>Le stock de licences pour <b>{title}</b> est passé sous le seuil d'alerte (mouvement effectué par <b>{actor}</b>).</p>
<table style="width:100%;border-collapse:collapse;margin-top:12px;font-size:13px;">
  <tr><td style="padding:6px 0;color:#64748b;width:220px;">Licences restantes</td><td style="padding:6px 0;"><b>{remaining} / {total}</b></td></tr>
  <tr><td style="padding:6px 0;color:#64748b;">Licences utilisées</td><td style="padding:6px 0;">{used}</td></tr>
  <tr><td style="padding:6px 0;color:#64748b;">Seuil configuré</td><td style="padding:6px 0;">{threshold}</td></tr>
</table>
<p style="margin-top:14px;">Pensez à commander de nouvelles licences pour éviter toute rupture côté équipe.</p>
<p style="color:#64748b;font-size:13px;margin-top:14px;">L'équipe {brand}</p>`,
  purchase_order_created_subject: `[{brand}] 🛒 Nouvelle demande — {client_name} ({products_summary})`,
  purchase_order_created_message: `<p>Bonjour,</p>
<p><b>{requester_name}</b> vient de déposer une nouvelle demande de commande pour le client <b>{client_name}</b>.</p>
{products_html}
<table style="width:100%;border-collapse:collapse;margin-top:12px;font-size:13px;">
  <tr><td style="padding:6px 0;color:#64748b;width:180px;">N° facture / devis</td><td style="padding:6px 0;font-family:monospace">{invoice}</td></tr>
  <tr><td style="padding:6px 0;color:#64748b;">Date de la demande</td><td style="padding:6px 0;">{requested_at}</td></tr>
  <tr><td style="padding:6px 0;color:#64748b;vertical-align:top">Notes</td><td style="padding:6px 0;white-space:pre-wrap">{notes}</td></tr>
</table>
<p style="color:#64748b;font-size:13px;margin-top:14px;">Rendez-vous dans le module <b>Commandes</b> pour valider et marquer comme commandée.</p>
<p style="color:#64748b;font-size:13px;margin-top:14px;">L'équipe {brand}</p>`,
  purchase_order_ordered_subject: `[{brand}] ✅ Commande passée — {product_type} ({client_name})`,
  purchase_order_ordered_message: `<p>Bonjour <b>{requester_name}</b>,</p>
<p>La commande que tu as demandée pour <b>{client_name}</b> vient d'être passée par <b>{ordered_by}</b>.</p>
<table style="width:100%;border-collapse:collapse;margin-top:12px;font-size:13px;">
  <tr><td style="padding:6px 0;color:#64748b;width:180px;">Produit</td><td style="padding:6px 0;"><b>{product_type}</b></td></tr>
  <tr><td style="padding:6px 0;color:#64748b;">Client</td><td style="padding:6px 0;">{client_name}</td></tr>
  <tr><td style="padding:6px 0;color:#64748b;">N° facture / devis</td><td style="padding:6px 0;font-family:monospace">{invoice}</td></tr>
  <tr><td style="padding:6px 0;color:#64748b;">Date de commande</td><td style="padding:6px 0;">{ordered_at}</td></tr>
  <tr><td style="padding:6px 0;color:#64748b;vertical-align:top">Info de suivi</td><td style="padding:6px 0;white-space:pre-wrap">{tracking_info}</td></tr>
</table>
<p style="color:#64748b;font-size:13px;margin-top:14px;">L'équipe {brand}</p>`,
};

export default function Settings() {
  const [sla, setSla] = useState(null);
  const [mail, setMail] = useState(null);
  const [sig, setSig] = useState(null);
  const [portal, setPortal] = useState(null);
  // v248 — WhatsApp default templates (used as placeholder + "Utiliser le défaut" button).
  const [portalDefaults, setPortalDefaults] = useState({ templates: {}, variables: [] });
  const [busy, setBusy] = useState(false);
  const [searchParams] = useSearchParams();
  const [webhookCfg, setWebhookCfg] = useState(null);
  const [secretVisible, setSecretVisible] = useState(false);

  const loadWebhookCfg = async () => {
    try { const r = await api.get("/webhooks/config"); setWebhookCfg(r.data); } catch { /* noop */ }
  };
  const regenerateWebhookSecret = async () => {
    if (!confirm("Régénérer la clé secrète ? L'ancienne sera immédiatement invalidée.")) return;
    try {
      await api.post("/webhooks/regenerate");
      await loadWebhookCfg();
      toast.success("Clé régénérée");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const toggleWebhook = async (enabled) => {
    try {
      await api.patch("/webhooks/config", { enabled });
      await loadWebhookCfg();
      toast.success(enabled ? "Webhook activé" : "Webhook désactivé");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  useEffect(() => { loadWebhookCfg(); }, []);

  useEffect(() => {
    Promise.all([
      api.get("/settings/sla"),
      api.get("/settings/imap"),
      api.get("/settings/signature"),
      api.get("/settings/portal"),
    ]).then(([s, i, g, p]) => {
      setSla(s.data); setMail(i.data); setSig(g.data); setPortal(p.data);
      const prov = i.data?.provider;
      if ((prov === "graph_delegated" && i.data?.refresh_token_set) || (prov === "graph_app" && i.data?.client_id)) {
        loadInboxStats();
      }
    });
    // v248 — Fetch WhatsApp default templates in parallel (silent on failure).
    api.get("/settings/whatsapp/templates/defaults")
       .then((d) => setPortalDefaults(d.data || { templates: {}, variables: [] }))
       .catch(() => {});
    // Handle Graph OAuth callback flash
    if (searchParams.get("graph_ok")) toast.success("Microsoft 365 connecté avec succès");
    if (searchParams.get("graph_error")) toast.error(`Erreur Microsoft 365 : ${searchParams.get("graph_error")}`);
    if (searchParams.get("graph_admin_consent_ok")) toast.success("Consentement admin accordé pour le tenant — vous pouvez maintenant relancer l'autorisation utilisateur si nécessaire");
  }, [searchParams]);

  const savePortal = async () => {
    try {
      const r = await api.put("/settings/portal", {
        brand_name: (portal.brand_name || "MasterTicket").trim() || "MasterTicket",
        support_phone: portal.support_phone || "",
        virtual_assistant_phone: portal.virtual_assistant_phone || "",
        auto_close_enabled: portal.auto_close_enabled ?? true,
        auto_close_days: parseInt(portal.auto_close_days || 0, 10),
        auto_close_reminder_enabled: portal.auto_close_reminder_enabled ?? true,
        auto_close_reminder_days: parseInt(portal.auto_close_reminder_days || 0, 10),
        auto_close_reminder_message: portal.auto_close_reminder_message || "",
        auto_close_message: portal.auto_close_message || "",
        notify_status_change_enabled: portal.notify_status_change_enabled !== false,
        ask_phone_enabled: portal.ask_phone_enabled === true,
        status_change_message: portal.status_change_message || "",
        ticket_created_message: portal.ticket_created_message || "",
        taken_over_message: portal.taken_over_message || "",
        new_reply_message: portal.new_reply_message || "",
        contact_info_message: portal.contact_info_message || "",
        hosting_info_message: portal.hosting_info_message || "",
        hosting_renewal_message: portal.hosting_renewal_message || "",
        domain_renewal_message: portal.domain_renewal_message || "",
        license_low_stock_enabled: portal.license_low_stock_enabled !== false,
        license_low_stock_subject: portal.license_low_stock_subject || "",
        license_low_stock_message: portal.license_low_stock_message || "",
        purchase_order_ordered_enabled: portal.purchase_order_ordered_enabled !== false,
        purchase_order_ordered_subject: portal.purchase_order_ordered_subject || "",
        purchase_order_ordered_message: portal.purchase_order_ordered_message || "",
        purchase_order_created_enabled: portal.purchase_order_created_enabled !== false,
        purchase_order_created_subject: portal.purchase_order_created_subject || "",
        purchase_order_created_message: portal.purchase_order_created_message || "",
        satisfaction_survey_enabled: !!portal.satisfaction_survey_enabled,
        satisfaction_survey_delay_days: parseInt(portal.satisfaction_survey_delay_days || 0, 10),
        survey_questions: (portal.survey_questions || []).map((q, i) => ({
          id: q.id, label: q.label, active: q.active !== false, order: i + 1,
        })),
        remote_assist_url: portal.remote_assist_url || "",
        form_alignment: portal.form_alignment || "center",
        form_side_image_url: portal.form_side_image_url || "",
        announcements: (portal.announcements || []).map((a, i) => ({
          id: a.id, message: a.message || "", severity: a.severity || "info",
          active: a.active !== false, order: i + 1,
          start_at: a.start_at || null, end_at: a.end_at || null,
          notified_at: a.notified_at || null,
        })),
        announcements_enabled: portal.announcements_enabled !== false,
        announcement_speed_seconds: parseInt(portal.announcement_speed_seconds || 30, 10),
        // v243 — WhatsApp settings must be included EXPLICITLY (savePortal used
        // to send a fixed field list, dropping unlisted keys via backend `$set`).
        whatsapp_sender_number: portal.whatsapp_sender_number || "",
        // v249 — Default OFF for signature check (broken behind K8s ingress
        // + Cloudflare reverse proxy). Admin must EXPLICITLY tick the box.
        whatsapp_verify_signature: portal.whatsapp_verify_signature === true,
        whatsapp_ack_message: portal.whatsapp_ack_message || "",
        whatsapp_taken_over_message: portal.whatsapp_taken_over_message || "",
        whatsapp_resolved_message: portal.whatsapp_resolved_message || "",
        whatsapp_reply_message: portal.whatsapp_reply_message || "",
        // v251 — IA NETKOM admin settings (same v243 pattern: fields must be
        // included EXPLICITLY, otherwise backend `$set` drops them and the
        // « Activer l'assistant IA » toggle reverts to default at reload).
        ia_netkom_enabled: portal.ia_netkom_enabled !== false,
        ia_netkom_model: portal.ia_netkom_model || "claude-sonnet-4-6",
        ia_daily_msg_limit: parseInt(portal.ia_daily_msg_limit ?? 30, 10),
        ia_monthly_cost_limit: parseFloat(portal.ia_monthly_cost_limit ?? 5),
        ia_fallback_enabled: portal.ia_fallback_enabled !== false,
      });
      setPortal(r.data); toast.success("Paramètres du portail enregistrés");
    } catch { toast.error("Erreur"); }
  };

  const addAnnouncement = () => setPortal({
    ...portal,
    announcements: [...(portal.announcements || []), {
      id: `a_${Date.now()}`, message: "", severity: "info", active: true,
      order: (portal.announcements?.length || 0) + 1,
    }],
  });
  const updateAnnouncement = (idx, patch) => {
    const list = [...(portal.announcements || [])];
    list[idx] = { ...list[idx], ...patch };
    setPortal({ ...portal, announcements: list });
  };
  const removeAnnouncement = (idx) => {
    const list = [...(portal.announcements || [])];
    list.splice(idx, 1);
    setPortal({ ...portal, announcements: list });
  };
  const moveAnnouncement = (idx, delta) => {
    const list = [...(portal.announcements || [])];
    const ni = idx + delta;
    if (ni < 0 || ni >= list.length) return;
    [list[idx], list[ni]] = [list[ni], list[idx]];
    setPortal({ ...portal, announcements: list });
  };

  const addQuestion = () => setPortal({
    ...portal,
    survey_questions: [...(portal.survey_questions || []), {
      id: `q_${Date.now()}`, label: "Nouvelle question", active: true, order: (portal.survey_questions?.length || 0) + 1,
    }],
  });
  const updateQuestion = (idx, patch) => {
    const qs = [...(portal.survey_questions || [])];
    qs[idx] = { ...qs[idx], ...patch };
    setPortal({ ...portal, survey_questions: qs });
  };
  const removeQuestion = (idx) => {
    const qs = [...(portal.survey_questions || [])];
    qs.splice(idx, 1);
    setPortal({ ...portal, survey_questions: qs });
  };
  const moveQuestion = (idx, delta) => {
    const qs = [...(portal.survey_questions || [])];
    const ni = idx + delta;
    if (ni < 0 || ni >= qs.length) return;
    [qs[idx], qs[ni]] = [qs[ni], qs[idx]];
    setPortal({ ...portal, survey_questions: qs });
  };

  const saveSig = async () => {
    try {
      const r = await api.put("/settings/signature", {
        html: sig.html || "", logo_url: sig.logo_url || "", prefill_reply: !!sig.prefill_reply,
      });
      setSig(r.data); toast.success("Signature enregistrée");
    } catch { toast.error("Erreur"); }
  };

  const saveSla = async () => {
    setBusy(true);
    try {
      const r = await api.put("/settings/sla", {
        response_minutes: sla.response_minutes, resolution_minutes: sla.resolution_minutes,
      });
      setSla(r.data); toast.success("SLA enregistrés");
    } catch { toast.error("Erreur"); }
    finally { setBusy(false); }
  };

  const saveMail = async (silent = false) => {
    try {
      await api.put("/settings/imap", {
        provider: mail.provider || "imap",
        host: mail.host, port: parseInt(mail.port || 993, 10),
        user: mail.user, password: mail.password || "",
        tenant_id: (mail.tenant_id || "").trim(),
        client_id: (mail.client_id || "").trim(),
        client_secret: mail.client_secret || "",
        mailbox: (mail.mailbox || "").trim(),
        // v258 — Pre-fill Microsoft login screen with the expected mailbox
        mailbox_to_authorize: (mail.mailbox_to_authorize || "").trim(),
        poll_interval: parseInt(mail.poll_interval || 60, 10),
        active: !!mail.active,
        post_import_action: mail.post_import_action || "keep",
        spam_filter_enabled: mail.spam_filter_enabled !== false,
        spam_keywords: (mail.spam_keywords_text || (mail.spam_keywords || []).join("\n"))
          .split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean),
        spam_rules: (mail.spam_rules || []).filter((r) => (r.value || "").trim()),
        ai_email_addresses: (mail.ai_email_addresses_text || (mail.ai_email_addresses || []).join("\n"))
          .split(/[\n,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean),
      });      if (!silent) toast.success("Boîte mail enregistrée");
      const r = await api.get("/settings/imap");
      setMail(r.data);
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || "Erreur");
      throw e;
    }
  };

  const rescanSpam = async () => {
    if (!window.confirm(
      "Réappliquer les règles anti-spam à tous les tickets email existants ?\n\n" +
      "Les tickets qui correspondent à vos règles actuelles seront déplacés dans « Courriers indésirables ».\n" +
      "Les tickets déjà marqués comme spam ne sont pas touchés."
    )) return;
    try {
      // Ensure the latest rules are persisted before scanning
      await saveMail(true);
      const r = await api.post("/settings/imap/rescan-spam");
      const d = r.data || {};
      toast.success(`Analyse : ${d.scanned} tickets scannés · ${d.flagged} marqué${d.flagged > 1 ? "s" : ""} comme spam`);
      if (d.samples && d.samples.length > 0) {
        console.info("Tickets nouvellement marqués comme spam:", d.samples);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la ré-analyse");
    }
  };

  const pollNow = async () => {
    try {
      const r = await api.post("/email/poll-now");
      const d = r.data;
      const inbox = d.inbox || {};
      const imported = Number(d.imported || 0);
      const threaded = Number(d.threaded || 0);
      const scanned = Number(d.scanned || 0);
      const bounce = Number(d.skipped_bounce || 0);
      const dedup = Number(d.skipped_dedup || 0);
      const recent = Number(d.recent_2min || 0);
      const stats = inbox.ok ? ` · boîte : ${inbox.total} mail(s) dont ${inbox.unread} non lu(s)` : "";
      if (d.error) {
        toast.error(`Erreur : ${d.error}${stats}`);
      } else if (imported > 0 || threaded > 0) {
        const parts = [];
        if (imported) parts.push(`${imported} nouveau${imported > 1 ? "x" : ""} ticket(s)`);
        if (threaded) parts.push(`${threaded} réponse(s) rattachée(s)`);
        toast.success(`${parts.join(" · ")}${stats}`);
      } else if (recent > 0) {
        toast.success(`Boîte à jour · ${recent} ticket(s) déjà créé(s) automatiquement il y a < 2 min${stats}`);
      } else if (scanned > 0) {
        // Scanned emails but none imported — surface why (bounce/dedup)
        const parts = [];
        if (bounce) parts.push(`${bounce} bounce/auto-reply`);
        if (dedup) {
          const dedupTickets = Array.isArray(d.dedup_tickets) ? d.dedup_tickets.slice(0, 5) : [];
          const suffix = dedupTickets.length ? ` (→ #${dedupTickets.join(", #")})` : "";
          parts.push(`${dedup} déjà connu(s)${suffix}`);
        }
        toast.warning(`${scanned} mail(s) analysé(s) · aucun importé (${parts.join(", ") || "cause inconnue"})${stats} — voir « Derniers mails ignorés »`);
        loadRecentSkips();
      } else {
        toast.info(`Boîte à jour · aucun nouveau mail non lu à importer${stats}`);
      }
      loadInboxStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Boîte mail non configurée"); }
  };

  const [recentSkips, setRecentSkips] = useState([]);
  const loadRecentSkips = async () => {
    try {
      const r = await api.get("/email/skips", { params: { limit: 20 } });
      setRecentSkips(r.data || []);
    } catch { setRecentSkips([]); }
  };

  const [inboxStats, setInboxStats] = useState(null);
  const [skipsOpen, setSkipsOpen] = useState(false);
  const [bootstrapOpen, setBootstrapOpen] = useState(false);
  const [bootstrapDays, setBootstrapDays] = useState(30);
  const [bootstrapFilter, setBootstrapFilter] = useState("unread");
  const [bootstrapBusy, setBootstrapBusy] = useState(false);

  const loadInboxStats = async () => {
    try {
      const r = await api.get("/graph/stats");
      if (r.data?.ok) setInboxStats({ total: r.data.total, unread: r.data.unread });
      else setInboxStats(null);
    } catch { setInboxStats(null); }
  };

  const runBootstrap = async () => {
    setBootstrapBusy(true);
    try {
      const r = await api.post("/graph/bootstrap", {
        days: Number(bootstrapDays) || 30,
        limit: 200,
        read_filter: bootstrapFilter,
      });
      const d = r.data || {};
      const imp = Number(d.imported || 0);
      const thr = Number(d.threaded || 0);
      const bou = Number(d.skipped_bounce || 0);
      const dup = Number(d.skipped_dedup || 0);
      const parts = [];
      if (imp) parts.push(`${imp} ticket(s) créé(s)`);
      if (thr) parts.push(`${thr} réponse(s) rattachée(s)`);
      if (bou) parts.push(`${bou} auto-reply ignoré(s)`);
      if (dup) parts.push(`${dup} déjà connu(s)`);
      const main = parts.join(" · ") || "aucun mail éligible";
      toast.success(`Synchro : ${d.scanned || 0} mail(s) analysé(s) · ${main}`);
      setBootstrapOpen(false);
      loadInboxStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Synchro échouée");
    } finally { setBootstrapBusy(false); }
  };

  const disconnectGraph = async () => {
    if (!window.confirm("Déconnecter le compte Microsoft actuel ?\n\nCela efface le refresh_token en base et désactive la récupération automatique. Vous devrez cliquer sur « Connecter Microsoft 365 » ensuite pour refaire un consentement neuf.")) return;
    try {
      await api.post("/graph/disconnect");
      toast.success("Compte Microsoft déconnecté — cliquez sur « Connecter Microsoft 365 » pour refaire un consentement neuf");
      // Refresh the mail settings state so the UI reflects the disconnect
      const r = await api.get("/settings/imap");
      setMail(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible de déconnecter"); }
  };

  const adminConsent = async () => {
    try {
      await saveMail(true);
      const r = await api.get("/graph/admin-consent-url");
      window.open(r.data.url, "_blank", "noopener,noreferrer");
      toast.info("Ouvrez le lien avec un compte admin de votre tenant. Une fois accepté, cliquez sur « Changer de compte » pour finaliser.");
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible de générer le lien"); }
  };

  const connectGraph = async () => {
    // Guard: validate tenant_id and client_id before redirecting to Microsoft
    const guid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const domain = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i;
    const tid = (mail.tenant_id || "").trim();
    const cid = (mail.client_id || "").trim();
    if (!tid || (!guid.test(tid) && !domain.test(tid) && tid !== "common" && tid !== "organizations")) {
      toast.error("Tenant ID invalide — collez le GUID (ou le domaine .onmicrosoft.com) depuis Azure Portal → Entra ID → Overview.");
      return;
    }
    if (!cid || !guid.test(cid)) {
      toast.error("Client ID (Application ID) invalide — c'est un GUID au format xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx.");
      return;
    }
    if (!mail.client_secret && !mail.client_secret_set) {
      toast.error("Client Secret manquant — générez-le dans « Certificates & secrets » de votre App Registration.");
      return;
    }
    try {
      // Persist current fields before redirect so the callback has the right creds
      await saveMail(true);
      const r = await api.get("/graph/authorize-url");
      window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible de lancer l'autorisation"); }
  };

  const testGraph = async () => {
    try {
      const r = await api.post("/graph/test");
      if (r.data.ok) toast.success(`Microsoft 365 OK · ${r.data.unread_sample ?? 0} message(s) non lus détectés`);
      else toast.error(r.data.reason || "Test échoué");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (!sla || !mail || !sig || !portal) return <div className="text-muted-foreground">Chargement…</div>;

  const provider = mail.provider || "imap";
  const isGraph = provider === "graph_app" || provider === "graph_delegated";

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <div className="label-caps">Configuration</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Paramètres</h1>
      </div>

      <Tabs defaultValue="sla" className="w-full">
        <TabsList className="flex flex-wrap gap-1 h-auto mb-4 p-1" data-testid="settings-tabs">
          <TabsTrigger value="sla" data-testid="tab-sla"><Clock size={14} className="mr-1.5"/> SLA</TabsTrigger>
          <TabsTrigger value="mail" data-testid="tab-mail"><EnvelopeOpen size={14} className="mr-1.5"/> Boîte mail</TabsTrigger>
          <TabsTrigger value="portal" data-testid="tab-portal"><Phone size={14} className="mr-1.5"/> Portail</TabsTrigger>
          <TabsTrigger value="survey" data-testid="tab-survey"><Star size={14} className="mr-1.5"/> Sondage</TabsTrigger>
          <TabsTrigger value="escalations" data-testid="tab-escalations"><Warning size={14} className="mr-1.5"/> Escalades</TabsTrigger>
          <TabsTrigger value="push" data-testid="tab-push"><BellRinging size={14} className="mr-1.5"/> Push</TabsTrigger>
          <TabsTrigger value="signature" data-testid="tab-signature"><Signature size={14} className="mr-1.5"/> Signature</TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="tab-webhooks"><PlugsConnected size={14} className="mr-1.5"/> Webhooks</TabsTrigger>
          <TabsTrigger value="ia-netkom" data-testid="tab-ia-netkom"><Robot size={14} className="mr-1.5"/> Assistant IA</TabsTrigger>
          <TabsTrigger value="mail-templates" data-testid="tab-mail-templates"><EnvelopeSimple size={14} className="mr-1.5"/> Templates mail</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp"><Phone size={14} className="mr-1.5"/> WhatsApp</TabsTrigger>
          <TabsTrigger value="bug-reports" data-testid="tab-bug-reports"><Bug size={14} className="mr-1.5"/> Signaler un bug</TabsTrigger>
          <TabsTrigger value="documentation" data-testid="tab-documentation"><BookOpen size={14} className="mr-1.5"/> Documentation</TabsTrigger>
          <TabsTrigger value="retro-hebdo" data-testid="tab-retro-hebdo" asChild>
            <Link to="/parametres/retro-hebdo" className="flex items-center"><ChartBar size={14} className="mr-1.5"/> Rétro hebdo</Link>
          </TabsTrigger>
          <TabsTrigger value="about" data-testid="tab-about"><Info size={14} className="mr-1.5"/> À propos</TabsTrigger>
        </TabsList>

      <TabsContent value="sla" className="space-y-6">
      {/* SLA */}
      <Card>
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><Clock size={20}/> Contrats de niveau de service (SLA)</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-6">Temps maximum en minutes pour la prise en charge et la résolution, selon la priorité.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b">
                <tr>
                  <th className="text-left py-2">Priorité</th>
                  <th className="text-left py-2">Prise en charge (min)</th>
                  <th className="text-left py-2">Résolution (min)</th>
                </tr>
              </thead>
              <tbody>
                {PRIORITIES.map((p) => (
                  <tr key={p} className="border-b">
                    <td className="py-3"><span className={`prio-${p} px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider`}>{PRIORITY_LABEL[p]}</span></td>
                    <td className="py-3">
                      <Input type="number" min={1} data-testid={`sla-response-${p}`}
                        value={sla.response_minutes[p] || 0}
                        onChange={(e) => setSla({...sla, response_minutes: {...sla.response_minutes, [p]: parseInt(e.target.value||"0", 10)}})}
                        className="max-w-[140px]"/>
                    </td>
                    <td className="py-3">
                      <Input type="number" min={1} data-testid={`sla-resolution-${p}`}
                        value={sla.resolution_minutes[p] || 0}
                        onChange={(e) => setSla({...sla, resolution_minutes: {...sla.resolution_minutes, [p]: parseInt(e.target.value||"0", 10)}})}
                        className="max-w-[140px]"/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 flex justify-end">
            <Button onClick={saveSla} disabled={busy} data-testid="save-sla">
              <FloppyDisk size={16} className="mr-2"/>{busy ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        </CardContent>
      </Card>
      </TabsContent>

      <TabsContent value="mail" className="space-y-6">
      {/* Mailbox */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <EnvelopeOpen size={20}/> Boîte mail support
            {mail.active && ((provider === "imap" && mail.host) || (isGraph && mail.client_id)) && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-normal ml-2"><PlugsConnected size={14}/> actif</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Sélectionnez votre fournisseur : IMAP classique, ou Microsoft 365 via Graph API pour les tenants modernes.
          </p>

          <div className="mb-4">
            <Label>Fournisseur</Label>
            <Select value={provider} onValueChange={(v) => setMail({...mail, provider: v})}>
              <SelectTrigger data-testid="mail-provider"><SelectValue/></SelectTrigger>
              <SelectContent>
                {PROVIDERS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {provider === "imap" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Serveur IMAP</Label>
                <Input data-testid="imap-host" value={mail.host || ""} onChange={(e) => setMail({...mail, host: e.target.value})} placeholder="imap.ovh.net"/>
              </div>
              <div>
                <Label>Port</Label>
                <Input type="number" data-testid="imap-port" value={mail.port || 993} onChange={(e) => setMail({...mail, port: e.target.value})}/>
              </div>
              <div>
                <Label>Utilisateur (adresse e-mail)</Label>
                <Input data-testid="imap-user" value={mail.user || ""} onChange={(e) => setMail({...mail, user: e.target.value})} placeholder="support@monentreprise.fr"/>
              </div>
              <div>
                <Label>Mot de passe {mail.password_set && <span className="text-xs text-muted-foreground">(déjà enregistré — laissez vide pour conserver)</span>}</Label>
                <Input type="password" data-testid="imap-password" value={mail.password || ""} onChange={(e) => setMail({...mail, password: e.target.value})} placeholder={mail.password_set ? "●●●●●●●●" : "Mot de passe d'application"}/>
              </div>
            </div>
          )}

          {isGraph && (
            <div className="space-y-4">
              <div className="p-4 rounded-md bg-blue-50 dark:bg-blue-950/20 border border-blue-200/60 text-sm flex items-start gap-3">
                <MicrosoftOutlookLogo size={20} className="text-blue-600 mt-0.5 shrink-0"/>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-blue-900 dark:text-blue-200">Configuration Microsoft 365 · Graph API</p>
                  <ol className="list-decimal ml-4 text-xs text-blue-800/90 dark:text-blue-200/80 mt-2 space-y-1">
                    <li>Allez sur <a href="https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noreferrer" className="underline font-medium">Entra ID → App registrations</a> et créez une nouvelle inscription.</li>
                    <li>Dans <b>Overview</b>, copiez le <b>Directory (tenant) ID</b> et l&apos;<b>Application (client) ID</b> ci-dessous (ce sont des GUIDs, pas des lettres au hasard).</li>
                    <li>Dans <b>Certificates & secrets → New client secret</b>, générez un secret et copiez la <b>Value</b> (pas le Secret ID).</li>
                    <li>Dans <b>API permissions</b>, ajoutez <code>Mail.ReadWrite</code> (délégué) ou <code>Mail.Read</code> (application) puis validez le consentement admin.</li>
                    <li className="break-all">Dans <b>Authentication → Add a platform → Web</b>, ajoutez ce Redirect URI exact :<br/>
                      <code className="inline-block mt-1 px-2 py-1 rounded bg-white/70 dark:bg-black/30 text-[11px] select-all break-all">{`${window.location.origin}/api/graph/callback`}</code>
                      <button type="button" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/api/graph/callback`); toast.success("Redirect URI copié"); }} className="ml-2 text-blue-700 hover:underline text-[11px]" data-testid="copy-redirect-uri">Copier</button>
                    </li>
                  </ol>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Tenant ID</Label>
                  <Input data-testid="graph-tenant" value={mail.tenant_id || ""} onChange={(e) => setMail({...mail, tenant_id: e.target.value})} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"/>
                </div>
                <div>
                  <Label>Client ID (Application ID)</Label>
                  <Input data-testid="graph-client-id" value={mail.client_id || ""} onChange={(e) => setMail({...mail, client_id: e.target.value})}/>
                </div>
                <div className="md:col-span-2">
                  <Label>Boîte à autoriser (Microsoft login) <span className="text-xs text-muted-foreground">— optionnel mais recommandé</span></Label>
                  <Input data-testid="graph-mailbox-to-authorize" value={mail.mailbox_to_authorize || ""}
                    onChange={(e) => setMail({...mail, mailbox_to_authorize: e.target.value})}
                    placeholder="support@netkom.fr"/>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Pré-remplit l&apos;écran Microsoft avec CET email lors du <b>Connecter</b> — évite de connecter par erreur ton compte admin personnel à la place de la boîte support partagée. Laissez vide si vous voulez choisir manuellement à chaque connexion.
                  </p>
                </div>
                <div>
                  <Label>Client Secret {mail.client_secret_set && <span className="text-xs text-muted-foreground">(déjà enregistré)</span>}</Label>
                  <Input type="password" data-testid="graph-client-secret" value={mail.client_secret || ""} onChange={(e) => setMail({...mail, client_secret: e.target.value})} placeholder={mail.client_secret_set ? "●●●●●●●●" : "Valeur du secret"}/>
                </div>
                {provider === "graph_app" && (
                  <div>
                    <Label>Boîte support (mailbox UPN)</Label>
                    <Input data-testid="graph-mailbox" value={mail.mailbox || ""} onChange={(e) => setMail({...mail, mailbox: e.target.value})} placeholder="support@monentreprise.onmicrosoft.com"/>
                  </div>
                )}
                {provider === "graph_delegated" && (
                  <div className="md:col-span-2 text-xs text-muted-foreground italic px-1">
                    <b>Note :</b> en mode délégué, MasterTicket lit la boîte de l&apos;utilisateur qui autorise l&apos;application. Le champ mailbox n&apos;est pas nécessaire.
                  </div>
                )}
              </div>

              {provider === "graph_delegated" && (
                <div className="p-3 rounded-md border bg-muted/30 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    {mail.refresh_token_set
                      ? <>
                          <CheckCircle size={16} className="text-emerald-600 shrink-0"/>
                          <div className="min-w-0">
                            <div className="font-medium text-emerald-800 dark:text-emerald-300">Autorisation Microsoft accordée</div>
                            {(mail.graph_user || mail.mailbox) && (
                              <div className="text-xs text-muted-foreground truncate" data-testid="graph-connected-mailbox">
                                Boîte connectée : <b className="text-foreground">{mail.graph_user || mail.mailbox}</b>
                              </div>
                            )}
                            {inboxStats && (
                              <div className="text-xs text-muted-foreground mt-0.5" data-testid="graph-inbox-stats">
                                <EnvelopeSimple size={12} className="inline mr-1 -mt-0.5"/>
                                Boîte de réception : <b className="text-foreground">{inboxStats.total}</b> mail(s) au total ·
                                <b className="text-foreground"> {inboxStats.unread}</b> non lu(s)
                              </div>
                            )}
                          </div>
                        </>
                      : <><XCircle size={16} className="text-orange-500 shrink-0"/> <span>Aucune autorisation active — cliquez sur « Connecter »</span></>
                    }
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="secondary" onClick={adminConsent} data-testid="graph-admin-consent">
                      <CheckCircle size={14} className="mr-1.5"/> Consentement admin
                    </Button>
                    <Button type="button" variant="secondary" onClick={connectGraph} data-testid="graph-connect">
                      <LinkIcon size={14} className="mr-1.5"/> {mail.refresh_token_set ? "Changer de compte" : "Connecter Microsoft 365"}
                    </Button>
                    {mail.refresh_token_set && (
                      <Button type="button" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={disconnectGraph} data-testid="graph-disconnect">
                        <XCircle size={14} className="mr-1.5"/> Déconnecter
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <Label>Intervalle de vérification (secondes)</Label>
              <Input type="number" min={30} data-testid="mail-interval" value={mail.poll_interval || 60} onChange={(e) => setMail({...mail, poll_interval: e.target.value})}/>
            </div>
            <div>
              <Label>Après import du mail</Label>
              <Select value={mail.post_import_action || "keep"} onValueChange={(v) => setMail({...mail, post_import_action: v})}>
                <SelectTrigger data-testid="mail-post-action"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep">Garder le mail (marqué comme lu)</SelectItem>
                  <SelectItem value="delete">Supprimer (déplacer vers Corbeille)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {(mail.post_import_action || "keep") === "delete"
                  ? "Les messages seront déplacés dans le dossier « Éléments supprimés » après création du ticket."
                  : "Les messages restent dans la boîte de réception, simplement marqués comme lus."}
              </p>
            </div>
            <div className="flex items-end gap-3 md:col-span-2">
              <Switch id="mail-active" data-testid="mail-active" checked={!!mail.active} onCheckedChange={(v) => setMail({...mail, active: v})}/>
              <Label htmlFor="mail-active" className="mb-1">Activer la récupération automatique</Label>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t space-y-3" data-testid="spam-block">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium text-sm">Filtrage des courriers indésirables</div>
                <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                  Les mails entrants dont le <b>sujet</b> contient l&apos;un des mots-clés ci-dessous seront créés en tant que ticket, mais placés directement dans l&apos;onglet <b>Courriers indésirables</b> de la page Tickets. Ils n&apos;apparaissent ni dans « Tous » ni dans « Mes tickets assignés ». La recherche se fait <b>uniquement dans le sujet</b> pour éviter les faux positifs — utilisez les règles avancées ci-dessous pour cibler le corps ou l&apos;expéditeur.
                </p>
              </div>
              <Switch id="mail-spam-enabled" data-testid="mail-spam-enabled"
                checked={mail.spam_filter_enabled !== false}
                onCheckedChange={(v) => setMail({...mail, spam_filter_enabled: v})}/>
            </div>
            <div>
              <Label htmlFor="mail-spam-keywords">Mots-clés (un par ligne, ou séparés par virgule / point-virgule)</Label>
              <textarea
                id="mail-spam-keywords"
                data-testid="mail-spam-keywords"
                className="w-full min-h-[120px] rounded-md border bg-background px-3 py-2 text-sm font-mono resize-y disabled:opacity-50"
                disabled={mail.spam_filter_enabled === false}
                placeholder={"viagra\nloterie\ngagné\npromotion exclusive\nnewsletter"}
                value={mail.spam_keywords_text ?? (mail.spam_keywords || []).join("\n")}
                onChange={(e) => setMail({...mail, spam_keywords_text: e.target.value})}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Comparaison insensible à la casse. Utilisez des mots ou expressions courtes. Cliquez sur « Enregistrer » pour appliquer.
              </p>
            </div>

            {/* Règles avancées — champ + type de match + valeur */}
            <div className="pt-4 border-t">
              <div className="font-medium text-sm mb-1">Règles avancées</div>
              <p className="text-xs text-muted-foreground mb-3 max-w-2xl">
                Filtrer par <b>expéditeur</b> précis, <b>domaine</b>, ou motif complexe. Chaque règle combine un <b>champ</b> (Email expéditeur / Domaine expéditeur / Sujet / Corps / Partout), un <b>type de comparaison</b> (contient / égale / commence par / regex) et une <b>valeur</b>.
              </p>
              <div className="space-y-2">
                {(mail.spam_rules || []).map((rule, idx) => (
                  <div key={idx} className="flex flex-wrap items-center gap-2 border rounded-md p-2 bg-muted/30" data-testid={`spam-rule-${idx}`}>
                    <input type="checkbox" checked={rule.enabled !== false}
                      onChange={(e) => {
                        const next = [...(mail.spam_rules || [])];
                        next[idx] = { ...next[idx], enabled: e.target.checked };
                        setMail({ ...mail, spam_rules: next });
                      }}
                      title={rule.enabled !== false ? "Règle active" : "Règle désactivée"}
                      className="accent-primary"
                      data-testid={`spam-rule-${idx}-enabled`}/>
                    <select value={rule.field || "anywhere"}
                      onChange={(e) => {
                        const next = [...(mail.spam_rules || [])];
                        next[idx] = { ...next[idx], field: e.target.value };
                        setMail({ ...mail, spam_rules: next });
                      }}
                      className="text-sm border rounded px-2 py-1 bg-background"
                      disabled={mail.spam_filter_enabled === false}
                      data-testid={`spam-rule-${idx}-field`}>
                      <option value="sender_email">Email expéditeur</option>
                      <option value="sender_domain">Domaine expéditeur</option>
                      <option value="subject">Sujet</option>
                      <option value="body">Corps</option>
                      <option value="anywhere">Partout</option>
                    </select>
                    <select value={rule.match || "contains"}
                      onChange={(e) => {
                        const next = [...(mail.spam_rules || [])];
                        next[idx] = { ...next[idx], match: e.target.value };
                        setMail({ ...mail, spam_rules: next });
                      }}
                      className="text-sm border rounded px-2 py-1 bg-background"
                      disabled={mail.spam_filter_enabled === false}
                      data-testid={`spam-rule-${idx}-match`}>
                      <option value="contains">contient</option>
                      <option value="equals">égale</option>
                      <option value="starts_with">commence par</option>
                      <option value="regex">regex</option>
                    </select>
                    <input type="text" value={rule.value || ""}
                      onChange={(e) => {
                        const next = [...(mail.spam_rules || [])];
                        next[idx] = { ...next[idx], value: e.target.value };
                        setMail({ ...mail, spam_rules: next });
                      }}
                      placeholder="valeur (ex. newsletter@… ou spammer.com)"
                      className="flex-1 min-w-[180px] text-sm border rounded px-2 py-1 bg-background font-mono"
                      disabled={mail.spam_filter_enabled === false}
                      data-testid={`spam-rule-${idx}-value`}/>
                    <button type="button"
                      onClick={() => {
                        const next = (mail.spam_rules || []).filter((_, i) => i !== idx);
                        setMail({ ...mail, spam_rules: next });
                      }}
                      className="text-destructive hover:bg-red-50 dark:hover:bg-red-950/30 rounded px-2 py-1 text-xs"
                      data-testid={`spam-rule-${idx}-remove`}>
                      Supprimer
                    </button>
                  </div>
                ))}
                <button type="button"
                  onClick={() => setMail({ ...mail, spam_rules: [...(mail.spam_rules || []), { enabled: true, field: "sender_domain", match: "contains", value: "" }] })}
                  className="text-xs px-3 py-1.5 border border-dashed rounded-md hover:bg-muted"
                  disabled={mail.spam_filter_enabled === false}
                  data-testid="spam-rule-add">
                  + Ajouter une règle
                </button>
              </div>
            </div>

            <div className="pt-4 border-t flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-medium text-sm">Réappliquer aux tickets existants</div>
                <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                  Les nouvelles règles ne s&apos;appliquent qu&apos;aux nouveaux emails. Cliquez ici pour <b>rescanner tous les tickets email déjà en base</b> et marquer comme spam ceux qui correspondent à vos règles actuelles. Les tickets déjà marqués comme spam ne sont pas touchés.
                </p>
              </div>
              <Button variant="secondary" size="sm" onClick={rescanSpam} data-testid="rescan-spam-btn"
                disabled={mail.spam_filter_enabled === false}>
                ↻ Réappliquer maintenant
              </Button>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t space-y-3">
            <div>
              <div className="font-medium text-sm mb-1 flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"/> Flux IA — sources qui doivent porter un badge « IA »
              </div>
              <p className="text-xs text-muted-foreground mb-2 max-w-2xl">
                Adresses email dont les tickets seront <b>automatiquement tagués « IA »</b> (bullet vert dans la liste des tickets + onglet de filtre dédié).
                Exemple : ton service qui transcrit les messages téléphoniques par IA, un chatbot externe, un secrétariat vocal automatisé, etc. Une adresse par ligne.
              </p>
              <textarea
                data-testid="mail-ai-emails"
                className="w-full min-h-[90px] rounded-md border bg-background px-3 py-2 text-sm font-mono resize-y"
                placeholder={"noreply@ai-transcript.io\nbot@monsecretaire.ai\ntranscription@allo.io"}
                value={mail.ai_email_addresses_text ?? (mail.ai_email_addresses || []).join("\n")}
                onChange={(e) => setMail({...mail, ai_email_addresses_text: e.target.value})}
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2 flex-wrap">
            <Button variant="secondary" onClick={() => { loadRecentSkips(); setSkipsOpen(true); }} data-testid="skips-open-btn">
              <EnvelopeOpen size={16} className="mr-2"/> Derniers mails ignorés
            </Button>
            <Button variant="secondary" onClick={pollNow} data-testid="poll-now-btn">
              <EnvelopeOpen size={16} className="mr-2"/> Vérifier maintenant
            </Button>
            {isGraph && (
              <>
                <Button variant="secondary" onClick={testGraph} data-testid="graph-test">
                  <MicrosoftOutlookLogo size={16} className="mr-2"/> Tester Graph
                </Button>
                <Button variant="secondary" onClick={() => setBootstrapOpen(true)} data-testid="graph-bootstrap-open">
                  <ArrowsClockwise size={16} className="mr-2"/> Synchroniser mails existants
                </Button>
              </>
            )}
            <Button onClick={saveMail} data-testid="save-mail"><FloppyDisk size={16} className="mr-2"/> Enregistrer</Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={bootstrapOpen} onOpenChange={setBootstrapOpen}>
        <DialogContent className="max-w-md" data-testid="bootstrap-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ArrowsClockwise size={20}/> Synchroniser les mails existants
            </DialogTitle>
            <DialogDescription>
              Aspire les mails de la boîte pour créer les tickets manquants. Les doublons sont détectés automatiquement (par ID de message) — un mail déjà importé n&apos;est jamais re-créé.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Période (nombre de jours en arrière)</Label>
              <Input type="number" min={1} max={365} value={bootstrapDays}
                onChange={(e) => setBootstrapDays(e.target.value)}
                data-testid="bootstrap-days"/>
              <p className="text-xs text-muted-foreground mt-1">Ex : 30 jours = mails reçus au cours du dernier mois.</p>
            </div>
            <div>
              <Label>Statut des mails à importer</Label>
              <Select value={bootstrapFilter} onValueChange={setBootstrapFilter}>
                <SelectTrigger data-testid="bootstrap-filter"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unread">Non lus uniquement</SelectItem>
                  <SelectItem value="read">Déjà lus uniquement</SelectItem>
                  <SelectItem value="all">Tous (lus + non lus)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {bootstrapFilter === "unread" && "Comme la synchro automatique — n\u2019importe que les mails jamais ouverts."}
                {bootstrapFilter === "read" && "Utile pour rattraper des mails déjà ouverts dans Outlook mais pas encore transformés en tickets."}
                {bootstrapFilter === "all" && "Import complet — mails lus et non lus. Recommandé pour le premier import."}
              </p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="secondary" onClick={() => setBootstrapOpen(false)} disabled={bootstrapBusy} data-testid="bootstrap-cancel">
              Annuler
            </Button>
            <Button onClick={runBootstrap} disabled={bootstrapBusy} data-testid="bootstrap-run">
              {bootstrapBusy ? "Synchronisation…" : "Lancer la synchro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={skipsOpen} onOpenChange={setSkipsOpen}>
        <DialogContent className="max-w-2xl" data-testid="skips-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <EnvelopeOpen size={20}/> Derniers mails ignorés
            </DialogTitle>
            <DialogDescription>
              Mails récupérés depuis la boîte mais <b>NON</b> transformés en ticket. Utile pour comprendre pourquoi un mail non lu ne remonte pas dans les tickets.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 max-h-[420px] overflow-y-auto -mx-4 px-4">
            {recentSkips.length === 0 && (
              <div className="text-sm text-muted-foreground py-8 text-center">Aucun mail ignoré récemment.</div>
            )}
            {recentSkips.length > 0 && (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground border-b">
                  <tr>
                    <th className="text-left py-2 pr-2">Quand</th>
                    <th className="text-left py-2 pr-2">Cause</th>
                    <th className="text-left py-2 pr-2">Expéditeur</th>
                    <th className="text-left py-2">Sujet</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSkips.map((s) => {
                    const badgeCls = s.kind === "bounce"
                      ? "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200"
                      : s.kind === "dedup"
                        ? "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
                        : s.kind === "no_sender"
                          ? "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                          : "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
                    return (
                      <tr key={s.id} className="border-b last:border-0" data-testid={`skip-row-${s.id}`}>
                        <td className="py-2 pr-2 mono text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(s.at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td className="py-2 pr-2">
                          <span className={`inline-block text-[0.65rem] px-2 py-0.5 rounded-full ${badgeCls}`} title={s.reason}>
                            {s.kind === "bounce" ? "Auto-reply" : s.kind === "dedup" ? "Doublon" : s.kind === "no_sender" ? "Sans expéditeur" : s.kind}
                          </span>
                          <div className="text-[0.7rem] text-muted-foreground mt-0.5">
                            {s.matched_ticket_id ? (
                              <>
                                déjà présent sur{" "}
                                <Link
                                  to={`/tickets/${s.matched_ticket_id}`}
                                  data-testid={`skip-open-ticket-${s.matched_ticket_number}`}
                                  className="text-primary hover:underline font-medium"
                                  onClick={() => setSkipsOpen(false)}
                                >
                                  #{s.matched_ticket_number}
                                </Link>
                                {s.matched_ticket_is_spam && (
                                  <span className="ml-1.5 inline-flex items-center gap-1 text-[0.65rem] px-1.5 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200 font-medium">
                                    ⚠ SPAM · règle « {s.matched_ticket_spam_reason} »
                                  </span>
                                )}
                                {!s.matched_ticket_is_spam && " — ouvrez-le pour comparer le contenu"}
                              </>
                            ) : s.reason}
                          </div>
                        </td>
                        <td className="py-2 pr-2 mono text-xs truncate max-w-[200px]" title={s.sender}>{s.sender || "—"}</td>
                        <td className="py-2 text-xs truncate max-w-[260px]" title={s.subject}>{s.subject || "(sans sujet)"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="secondary" onClick={loadRecentSkips} data-testid="skips-refresh">Actualiser</Button>
            <Button onClick={() => setSkipsOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </TabsContent>

      <TabsContent value="portal" className="space-y-6">
      {/* Portal (public phone + auto-close) */}
      <Card>
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><Phone size={20}/> Portail public & clôture automatique</CardTitle></CardHeader>
        <CardContent>
          {/* Announcements marquee */}
          <div className="mb-6 pb-6 border-b">
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <Switch id="ann-enabled" data-testid="announce-enabled"
                    checked={portal.announcements_enabled !== false}
                    onCheckedChange={(v) => setPortal({...portal, announcements_enabled: v})}/>
                  <Label htmlFor="ann-enabled" className="cursor-pointer flex items-center gap-2">
                    <Megaphone size={16} className="text-blue-500"/> Bandeau d&apos;annonces défilant
                  </Label>
                </div>
                <div className="text-xs text-muted-foreground mt-1 ml-11">
                  Interrupteur général : désactivez pour masquer complètement le bandeau, même si des messages sont saisis. Passer la souris sur le bandeau met en pause le défilement.
                </div>
              </div>
              <Button type="button" size="sm" variant="secondary" onClick={addAnnouncement}
                disabled={portal.announcements_enabled === false}
                data-testid="announce-add">
                <Plus size={14} className="mr-1"/> Ajouter
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
              <div>
                <Label>Durée d&apos;affichage par message (secondes)</Label>
                <Input type="number" min={3} max={60} data-testid="announce-speed"
                  value={portal.announcement_speed_seconds ?? 6}
                  onChange={(e) => setPortal({...portal, announcement_speed_seconds: e.target.value})}
                  className="max-w-xs"/>
                <div className="text-xs text-muted-foreground mt-1">Temps d&apos;affichage de chaque message avant le fondu enchaîné vers le suivant (min 3s).</div>
              </div>
            </div>
            <div className="space-y-2">
              {(portal.announcements || []).length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-4 border border-dashed rounded-md">
                  Aucun message. Cliquez sur « Ajouter » pour créer votre premier message.
                </div>
              )}
              {(portal.announcements || []).map((a, idx) => (
                <div key={a.id || idx} data-testid={`announce-${idx}`}
                     className={`space-y-2 p-2 rounded-md border ${a.active === false ? "opacity-50 bg-muted/30" : "bg-background"}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-6 text-center mono">{idx + 1}.</span>
                    <select value={a.severity || "info"} onChange={(e) => updateAnnouncement(idx, {severity: e.target.value})}
                      data-testid={`announce-severity-${idx}`}
                      className={`text-xs border rounded-md px-2 py-2 shrink-0 ${
                        a.severity === "critical" ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300"
                        : a.severity === "warning" ? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300"
                        : "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300"
                      }`}>
                      <option value="info">🟢 Succès (vert)</option>
                      <option value="warning">🟠 Avertissement (orange)</option>
                      <option value="critical">🔴 Critique (rouge)</option>
                    </select>
                    <Input value={a.message || ""} onChange={(e) => updateAnnouncement(idx, {message: e.target.value})}
                      className="flex-1" placeholder="Ex. Maintenance planifiée le 12/02 de 22h à 23h — coupures possibles"/>
                    <div className="flex items-center gap-1 shrink-0">
                      <Switch checked={a.active !== false}
                        onCheckedChange={(v) => updateAnnouncement(idx, {active: v})}
                        data-testid={`announce-active-${idx}`}/>
                      <Button type="button" size="icon" variant="ghost" onClick={() => moveAnnouncement(idx, -1)}
                        disabled={idx === 0}>
                        <ArrowUp size={14}/>
                      </Button>
                      <Button type="button" size="icon" variant="ghost" onClick={() => moveAnnouncement(idx, 1)}
                        disabled={idx === (portal.announcements?.length || 0) - 1}>
                        <ArrowDown size={14}/>
                      </Button>
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeAnnouncement(idx)}
                        data-testid={`announce-delete-${idx}`}>
                        <TrashSimple size={14} className="text-red-500"/>
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap pl-8 text-xs text-muted-foreground">
                    <span className="whitespace-nowrap">Programmation (facultatif) —</span>
                    <label className="inline-flex items-center gap-1">
                      Du
                      <input type="datetime-local"
                        data-testid={`announce-start-${idx}`}
                        value={(a.start_at || "").slice(0, 16)}
                        onChange={(e) => updateAnnouncement(idx, {start_at: e.target.value ? new Date(e.target.value).toISOString() : null})}
                        className="border rounded-md px-2 py-1 bg-background"/>
                    </label>
                    <label className="inline-flex items-center gap-1">
                      Au
                      <input type="datetime-local"
                        data-testid={`announce-end-${idx}`}
                        value={(a.end_at || "").slice(0, 16)}
                        onChange={(e) => updateAnnouncement(idx, {end_at: e.target.value ? new Date(e.target.value).toISOString() : null})}
                        className="border rounded-md px-2 py-1 bg-background"/>
                    </label>
                    {(a.start_at || a.end_at) && (
                      <button type="button" onClick={() => updateAnnouncement(idx, {start_at: null, end_at: null})}
                        className="text-red-500 hover:underline">Effacer les dates</button>
                    )}
                    {a.severity === "critical" && a.notified_at && (
                      <span className="ml-auto inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                        <BellRinging size={12}/> Push envoyé le {new Date(a.notified_at).toLocaleString("fr-FR")}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-sm text-muted-foreground mb-4">
            Numéro de téléphone affiché en haut du portail client et de l&apos;écran de connexion. La clôture automatique ferme les tickets restés sans mise à jour depuis N jours (0 = désactivée) et envoie un message d&apos;explication au client.
          </p>
          <div className="mb-6 p-4 rounded-lg border-2 border-dashed border-primary/30 bg-primary/5">
            <Label htmlFor="portal-brand-name" className="text-sm font-semibold">
              Nom de marque affiché dans les emails
            </Label>
            <p className="text-xs text-muted-foreground mt-1 mb-2">
              Apparaît dans le titre de chaque email envoyé au client (ex. <span className="mono">[{portal.brand_name || "MasterTicket"}] Ticket #123 enregistré</span>) ainsi que dans l&apos;en-tête HTML du mail (<i>{portal.brand_name || "MasterTicket"} · Support IT</i>).
            </p>
            <Input
              id="portal-brand-name"
              data-testid="portal-brand-name"
              maxLength={40}
              value={portal.brand_name || ""}
              onChange={(e) => setPortal({...portal, brand_name: e.target.value})}
              placeholder="MasterTicket"
              className="max-w-sm"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Ligne support (téléphone)</Label>
              <Input data-testid="portal-phone-input"
                value={portal.support_phone || ""}
                onChange={(e) => setPortal({...portal, support_phone: e.target.value})}
                placeholder="+33 1 23 45 67 89"/>
              <div className="text-xs text-muted-foreground mt-1">Affiché comme « Ligne support : … ». Vide pour masquer.</div>
            </div>
            <div>
              <Label>Assistant Virtuel 24h/24 (téléphone)</Label>
              <Input data-testid="portal-phone-virtual-input"
                value={portal.virtual_assistant_phone || ""}
                onChange={(e) => setPortal({...portal, virtual_assistant_phone: e.target.value})}
                placeholder="+33 9 87 65 43 21"/>
              <div className="text-xs text-muted-foreground mt-1">Affiché comme « Assistant Virtuel 24h/24 : … ». Vide pour masquer.</div>
            </div>
            <div className="md:col-span-2">
              <Label>Alignement du formulaire portail</Label>
              <select value={portal.form_alignment || "center"}
                onChange={(e) => setPortal({...portal, form_alignment: e.target.value})}
                data-testid="portal-form-alignment"
                className="w-full max-w-xs text-sm border rounded-md px-3 py-2 bg-background">
                <option value="left">← Gauche (image à droite)</option>
                <option value="center">Centré (sans image)</option>
                <option value="right">Droite (image à gauche) →</option>
              </select>
              <div className="text-xs text-muted-foreground mt-1">Position du formulaire sur le portail. Une image apparaît de l&apos;autre côté sur écran ≥ lg.</div>
            </div>
            <div className="md:col-span-2">
              <Label>Image latérale</Label>
              <div className="flex gap-2 items-start">
                <div className="flex-1">
                  <Input data-testid="portal-side-image-input"
                    value={portal.form_side_image_url || ""}
                    onChange={(e) => setPortal({...portal, form_side_image_url: e.target.value})}
                    placeholder="https://…/illustration.jpg  ou uploadée ci-contre"
                    disabled={(portal.form_alignment || "center") === "center"}/>
                </div>
                <input type="file" accept="image/*" className="hidden" id="side-image-upload"
                  onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const fd = new FormData(); fd.append("file", f);
                    try {
                      const r = await api.post("/settings/portal/side-image", fd, { headers: {"Content-Type": "multipart/form-data"} });
                      setPortal({...portal, form_side_image_url: r.data.url});
                      toast.success("Image téléversée");
                    } catch (err) { toast.error(err.response?.data?.detail || "Erreur upload"); }
                    e.target.value = "";
                  }}/>
                <Button type="button" variant="secondary"
                  onClick={() => document.getElementById("side-image-upload").click()}
                  disabled={(portal.form_alignment || "center") === "center"}
                  data-testid="portal-side-image-upload-btn">
                  Téléverser…
                </Button>
              </div>
              {portal.form_side_image_url && (portal.form_alignment || "center") !== "center" && (
                <img src={portal.form_side_image_url} alt="Aperçu" className="mt-2 max-h-32 rounded border"/>
              )}
              <div className="text-xs text-muted-foreground mt-1">URL externe OU téléversement (max 5 Mo). Masquée quand alignement Centré.</div>
            </div>
            <div className="md:col-span-2">
              <Label>Lien télémaintenance (bouée dans le header du portail)</Label>
              <Input data-testid="portal-remote-assist-input"
                value={portal.remote_assist_url || ""}
                onChange={(e) => setPortal({...portal, remote_assist_url: e.target.value})}
                placeholder="https://get.teamviewer.com/…  ou https://votre-outil-tele.example"/>
              <div className="text-xs text-muted-foreground mt-1">URL de votre logiciel de prise en main à distance. Une petite icône « bouée » cliquable apparaît à côté du bouton « Se connecter » du portail. Vide pour masquer.</div>
            </div>
            <div className="md:col-span-2 flex items-start justify-between gap-4 p-3 rounded-lg border bg-muted/20">
              <div>
                <Label htmlFor="portal-auto-close-toggle" className="font-medium cursor-pointer">Clôture automatique des tickets inactifs</Label>
                <div className="text-xs text-muted-foreground mt-0.5">Active/désactive complètement la clôture automatique et son email de notification client.</div>
              </div>
              <Switch id="portal-auto-close-toggle" data-testid="portal-auto-close-toggle"
                checked={portal.auto_close_enabled !== false}
                onCheckedChange={(v) => setPortal({...portal, auto_close_enabled: v})}/>
            </div>
            <div className="md:col-span-2">
              <Label>Clôture automatique après (jours)</Label>
              <Input type="number" min={0} data-testid="portal-auto-close-days"
                value={portal.auto_close_days ?? 0}
                onChange={(e) => setPortal({...portal, auto_close_days: e.target.value})}
                disabled={portal.auto_close_enabled === false}
                className="max-w-xs"/>
              <div className="text-xs text-muted-foreground mt-1">0 = désactivée. Concerne les tickets ouverts par un client (web/email).</div>
            </div>
            <div className="md:col-span-2 flex items-start justify-between gap-4 p-3 rounded-lg border bg-muted/20">
              <div>
                <Label htmlFor="portal-reminder-toggle" className="font-medium cursor-pointer">Envoyer un rappel avant clôture</Label>
                <div className="text-xs text-muted-foreground mt-0.5">Envoie automatiquement un email au client N jours avant la clôture pour lui laisser l&apos;occasion de répondre.</div>
              </div>
              <Switch id="portal-reminder-toggle" data-testid="portal-reminder-toggle"
                checked={portal.auto_close_reminder_enabled !== false}
                onCheckedChange={(v) => setPortal({...portal, auto_close_reminder_enabled: v})}
                disabled={portal.auto_close_enabled === false}/>
            </div>
            <div className="md:col-span-2">
              <Label>Rappel client avant clôture (jours)</Label>
              <Input type="number" min={0} data-testid="portal-auto-close-reminder-days"
                value={portal.auto_close_reminder_days ?? 0}
                onChange={(e) => setPortal({...portal, auto_close_reminder_days: e.target.value})}
                disabled={portal.auto_close_enabled === false || portal.auto_close_reminder_enabled === false}
                className="max-w-xs"/>
              <div className="text-xs text-muted-foreground mt-1">Envoie un rappel N jours avant la clôture automatique. 0 = désactivé. Doit être &lt; clôture automatique.</div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground italic mt-4 p-3 rounded-lg border bg-muted/20">
            💡 Le contenu des emails <b>Rappel avant clôture</b>, <b>Clôture automatique</b> et <b>Changement de statut</b> se personnalise désormais dans l&apos;onglet <b>Templates mail</b> ci-dessus.
          </p>

          {/* --- Ask-phone toggle --- */}
          <div className="mt-6 pt-6 border-t">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="portal-askphone-toggle" className="font-medium cursor-pointer">Demander le n° de téléphone au client à la création du ticket</Label>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Envoie un email au client (uniquement s&apos;il n&apos;a pas encore de numéro) pour lui demander de nous le communiquer. Désactivez si vous ne voulez plus ce mail systématique.
                </div>
              </div>
              <Switch id="portal-askphone-toggle" data-testid="portal-askphone-toggle"
                checked={portal.ask_phone_enabled === true}
                onCheckedChange={(v) => setPortal({...portal, ask_phone_enabled: v})}/>
            </div>
          </div>

          {/* --- Status-change toggle only (template moved to Templates mail tab) --- */}
          <div className="mt-6 pt-6 border-t">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="portal-status-change-toggle" className="font-medium cursor-pointer">Notification client à chaque changement de statut</Label>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Envoie un email au client dès qu&apos;un ticket passe à <b>En cours</b> ou <b>En attente</b>, avec le nom du technicien en charge. Ignoré pour <i>Résolu</i> (sondage) et <i>Fermé</i> (email dédié). Le contenu se personnalise dans <b>Templates mail</b>.
                </div>
              </div>
              <Switch id="portal-status-change-toggle" data-testid="portal-status-change-toggle"
                checked={portal.notify_status_change_enabled !== false}
                onCheckedChange={(v) => setPortal({...portal, notify_status_change_enabled: v})}/>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t">
            {/* Survey moved to dedicated tab — see the "Sondage" tab */}
            <p className="text-xs text-muted-foreground italic">
              La configuration du sondage de satisfaction est désormais dans l&apos;onglet <b>Sondage</b> ci-dessus.
            </p>
          </div>

          <div className="mt-6 flex justify-end">
            <Button onClick={savePortal} data-testid="save-portal"><FloppyDisk size={16} className="mr-2"/> Enregistrer</Button>
          </div>
        </CardContent>
      </Card>
      </TabsContent>

      <TabsContent value="push" className="space-y-6">
      {/* Push notifications */}
      <Card>
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><BellRinging size={20}/> Notifications push (PWA)</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Recevez les alertes de nouvelles affectations et de tickets urgents en temps réel, sur tous vos appareils (mobile, desktop). Installez MasterTicket sur votre écran d&apos;accueil pour un accès instantané.
          </p>
          <PushSetup/>
        </CardContent>
      </Card>
      </TabsContent>

      <TabsContent value="escalations" className="space-y-6">
        <Escalations/>
      </TabsContent>

      <TabsContent value="survey" className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2">
              <Star size={20} weight="fill" className="text-amber-500"/> Sondage de satisfaction
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Envoyez automatiquement un sondage au client quelques jours après la clôture d&apos;un ticket. Les notes remontent dans « Stats techniciens » et permettent de mesurer la qualité du support par technicien.
            </p>

            <div className="flex items-center gap-3 mb-3">
              <Switch id="survey-enabled-t" data-testid="survey-enabled"
                checked={!!portal.satisfaction_survey_enabled}
                onCheckedChange={(v) => setPortal({...portal, satisfaction_survey_enabled: v})}/>
              <Label htmlFor="survey-enabled-t" className="cursor-pointer">
                Activer l&apos;envoi automatique du sondage
              </Label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Délai après clôture (jours)</Label>
                <Input type="number" min={0} data-testid="survey-delay-days"
                  value={portal.satisfaction_survey_delay_days ?? 3}
                  onChange={(e) => setPortal({...portal, satisfaction_survey_delay_days: e.target.value})}
                  className="max-w-xs"
                  disabled={!portal.satisfaction_survey_enabled}/>
                <div className="text-xs text-muted-foreground mt-1">Le sondage est envoyé N jours après la clôture (ou la résolution si non-fermé).</div>
              </div>
              <div>
                <Label>Style des icônes de notation</Label>
                <select
                  className="mt-1 w-full h-9 rounded-md border bg-background px-3 text-sm max-w-xs"
                  data-testid="survey-scale-style"
                  value={portal.survey_scale_style || "stars"}
                  onChange={(e) => setPortal({...portal, survey_scale_style: e.target.value})}
                  disabled={!portal.satisfaction_survey_enabled}
                >
                  <option value="stars">⭐ Étoiles (classique)</option>
                  <option value="smileys">😀 Émojis visages (😠 😕 😐 🙂 😍)</option>
                  <option value="hearts">💖 Cœurs (💔 😔 🙂 😊 ❤️)</option>
                  <option value="thumbs">👍 Pouces (👎 👎 👐 👍 👍)</option>
                </select>
                <div className="text-xs text-muted-foreground mt-1">Choisissez le style des 5 icônes affichées au client pour noter.</div>
              </div>
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <Label className="flex items-center gap-2"><Star size={16} weight="fill" className="text-amber-500"/> Questions du sondage (notation 1 à 5 étoiles)</Label>
                  <div className="text-xs text-muted-foreground mt-1">La note globale du ticket est la moyenne des réponses. Chaque question apparaît dans « Stats techniciens ».</div>
                </div>
                <Button type="button" size="sm" variant="secondary" onClick={addQuestion}
                  disabled={!portal.satisfaction_survey_enabled} data-testid="survey-add-question">
                  <Plus size={14} className="mr-1"/> Ajouter
                </Button>
              </div>
              <div className="space-y-2">
                {(portal.survey_questions || []).length === 0 && (
                  <div className="text-sm text-muted-foreground text-center py-4 border border-dashed rounded-md">
                    Aucune question. Cliquez sur « Ajouter » pour créer votre première question.
                  </div>
                )}
                {(portal.survey_questions || []).map((q, idx) => (
                  <div key={q.id || idx} data-testid={`survey-q-${idx}`}
                       className={`flex items-center gap-2 p-2 rounded-md border ${q.active === false ? "opacity-50 bg-muted/30" : "bg-background"}`}>
                    <span className="text-xs text-muted-foreground w-6 text-center mono">{idx + 1}.</span>
                    <Input value={q.label || ""} onChange={(e) => updateQuestion(idx, {label: e.target.value})}
                      disabled={!portal.satisfaction_survey_enabled}
                      className="flex-1" placeholder="Ex. Rapidité de prise en charge"/>
                    <div className="flex items-center gap-1 shrink-0">
                      <Switch checked={q.active !== false}
                        onCheckedChange={(v) => updateQuestion(idx, {active: v})}
                        disabled={!portal.satisfaction_survey_enabled}
                        data-testid={`survey-q-active-${idx}`}/>
                      <Button type="button" size="icon" variant="ghost" onClick={() => moveQuestion(idx, -1)}
                        disabled={idx === 0 || !portal.satisfaction_survey_enabled}>
                        <ArrowUp size={14}/>
                      </Button>
                      <Button type="button" size="icon" variant="ghost" onClick={() => moveQuestion(idx, 1)}
                        disabled={idx === (portal.survey_questions?.length || 0) - 1 || !portal.satisfaction_survey_enabled}>
                        <ArrowDown size={14}/>
                      </Button>
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeQuestion(idx)}
                        disabled={!portal.satisfaction_survey_enabled} data-testid={`survey-q-delete-${idx}`}>
                        <TrashSimple size={14} className="text-red-500"/>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button
                type="button" variant="secondary"
                data-testid="survey-test-email"
                onClick={async () => {
                  try { await api.put("/settings/portal", portal); } catch { /* noop */ }
                  try {
                    const r = await api.post("/settings/test-email/survey");
                    toast.success(`Email test envoyé à ${r.data.sent_to}`);
                  } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'envoi"); }
                }}
              >
                <EnvelopeSimple size={16} className="mr-2"/> Envoyer un email test
              </Button>
              <Button onClick={savePortal} data-testid="save-survey"><FloppyDisk size={16} className="mr-2"/> Enregistrer</Button>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="signature" className="space-y-6">
      {/* Signature */}
      <Card>
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><Signature size={20}/> Signature entreprise (globale)</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Ajoutée automatiquement en pied de tous les e-mails sortants (confirmations, réponses, résolutions, auto-réponses).
            Elle apparaît <b>en dessous</b> de la signature personnelle du technicien qui répond.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Logo de l&apos;entreprise (optionnel)</Label>
              <div className="mt-1 flex flex-col gap-2">
                {sig.logo_url && (
                  <div className="flex items-center gap-3 border rounded-md p-2 bg-muted/30">
                    <img src={sig.logo_url} alt="Logo" className="h-14 max-w-[160px] object-contain bg-white rounded"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-muted-foreground truncate mono">{sig.logo_url.split("/").pop()}</div>
                    </div>
                    <Button
                      type="button" variant="ghost" size="sm"
                      data-testid="sig-logo-remove"
                      onClick={async () => {
                        try {
                          const r = await api.put("/settings/signature", { ...sig, logo_url: "", logo_path: "" });
                          setSig(r.data); toast.success("Logo supprimé");
                        } catch { toast.error("Erreur"); }
                      }}
                    >
                      <TrashSimple size={14}/>
                    </Button>
                  </div>
                )}
                <label className="cursor-pointer inline-flex items-center gap-2 border-2 border-dashed rounded-md px-3 py-3 text-sm hover:bg-muted/30 justify-center" data-testid="sig-logo-dropzone">
                  <input
                    type="file" accept="image/*" className="hidden"
                    data-testid="sig-logo-input"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 3 * 1024 * 1024) { toast.error("Logo trop volumineux (max 3 Mo)"); return; }
                      const fd = new FormData();
                      fd.append("file", f);
                      try {
                        const r = await api.post("/settings/signature/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                        setSig((s) => ({ ...s, logo_url: r.data.url }));
                        toast.success("Logo téléchargé");
                      } catch (err) { toast.error(err.response?.data?.detail || "Erreur upload"); }
                      finally { e.target.value = ""; }
                    }}
                  />
                  📎 {sig.logo_url ? "Remplacer le logo" : "Choisir un logo (PNG/JPG · max 3 Mo)"}
                </label>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <Switch id="sig-prefill" data-testid="sig-prefill" checked={!!sig.prefill_reply} onCheckedChange={(v) => setSig({...sig, prefill_reply: v})}/>
                <Label htmlFor="sig-prefill">Pré-remplir le composeur de réponse aussi</Label>
              </div>
            </div>
            <div>
              <Label>Aperçu</Label>
              <div className="border rounded-md p-3 bg-muted/30 mt-1 min-h-[100px]">
                {sig.logo_url && <img src={sig.logo_url} alt="Logo" className="h-10 mb-2 object-contain"/>}
                <RichView html={sig.html}/>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <Label>Contenu de la signature (HTML)</Label>
            <div className="mt-1">
              <RichEditor value={sig.html} onChange={(v) => setSig({...sig, html: v})} minHeight={140}/>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <Button onClick={saveSig} data-testid="save-sig"><FloppyDisk size={16} className="mr-2"/> Enregistrer</Button>
          </div>
        </CardContent>
      </Card>
      </TabsContent>

      <TabsContent value="ia-netkom" className="space-y-6">
        <IaNetkomAdminCard portal={portal} setPortal={setPortal} savePortal={savePortal}/>
      </TabsContent>

      <TabsContent value="mail-templates" className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2"><EnvelopeSimple size={20}/> Templates mail</CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            <p className="text-sm text-muted-foreground">
              Personnalisez le contenu HTML des emails envoyés à vos clients. Utilisez les <b>variables entre accolades</b> — elles sont remplacées automatiquement. Laissez un template vide pour revenir au contenu par défaut. Chaque template a un bouton <b>Envoyer un email test</b> qui vous l&apos;envoie à votre adresse pour aperçu.
            </p>

            {/* 1. Ticket créé */}
            <div className="pt-2">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-base">1. Confirmation de création de ticket</Label>
                <span className="text-[0.65rem] mono px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">envoyé à chaque création</span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Variables : <code>{"{client_name}"}</code>, <code>{"{ticket_number}"}</code>, <code>{"{ticket_subject}"}</code>, <code>{"{priority}"}</code>, <code>{"{category}"}</code>, <code>{"{brand}"}</code>
              </p>
              <RichEditor value={portal.ticket_created_message || ""}
                onChange={(v) => setPortal({...portal, ticket_created_message: v})} minHeight={140}/>
              <div className="mt-2 flex justify-end gap-2">
                {true && (
                  <Button type="button" variant="outline" size="sm" data-testid="tmpl-default-created"
                    onClick={() => setPortal({...portal, ticket_created_message: DEFAULT_TEMPLATES.ticket_created_message})}>
                    Charger le modèle par défaut
                  </Button>
                )}
                <Button type="button" variant="secondary" size="sm" data-testid="tmpl-test-created"
                  onClick={async () => {
                    try { await api.put("/settings/portal", portal); } catch { /* noop */ }
                    try { const r = await api.post("/settings/test-email/created"); toast.success(`Envoyé à ${r.data.sent_to}`); }
                    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  }}>
                  <EnvelopeSimple size={14} className="mr-1.5"/> Envoyer un email test
                </Button>
              </div>
            </div>

            {/* 2. Prise en charge */}
            <div className="pt-6 border-t">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-base">2. Prise en charge par un technicien</Label>
                <span className="text-[0.65rem] mono px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">envoyé à l&apos;assignation</span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Variables : <code>{"{client_name}"}</code>, <code>{"{ticket_number}"}</code>, <code>{"{ticket_subject}"}</code>, <code>{"{tech_name}"}</code>, <code>{"{tech_phone}"}</code>, <code>{"{brand}"}</code>
                <br/><i>Le numéro direct provient du champ « Numéro direct » de la signature du technicien (Ma signature).</i>
              </p>
              <RichEditor value={portal.taken_over_message || ""}
                onChange={(v) => setPortal({...portal, taken_over_message: v})} minHeight={140}/>
              <div className="mt-2 flex justify-end gap-2">
                {true && (
                  <Button type="button" variant="outline" size="sm" data-testid="tmpl-default-taken-over"
                    onClick={() => setPortal({...portal, taken_over_message: DEFAULT_TEMPLATES.taken_over_message})}>
                    Charger le modèle par défaut
                  </Button>
                )}
                <Button type="button" variant="secondary" size="sm" data-testid="tmpl-test-taken-over"
                  onClick={async () => {
                    try { await api.put("/settings/portal", portal); } catch { /* noop */ }
                    try { const r = await api.post("/settings/test-email/taken-over"); toast.success(`Envoyé à ${r.data.sent_to}`); }
                    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  }}>
                  <EnvelopeSimple size={14} className="mr-1.5"/> Envoyer un email test
                </Button>
              </div>
            </div>

            {/* 3. Nouvelle réponse */}
            <div className="pt-6 border-t">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-base">3. Nouvelle réponse du technicien</Label>
                <span className="text-[0.65rem] mono px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">envoyé à chaque commentaire public</span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Variables : <code>{"{client_name}"}</code>, <code>{"{ticket_number}"}</code>, <code>{"{ticket_subject}"}</code>, <code>{"{author_name}"}</code>, <code>{"{comment_body}"}</code>, <code>{"{brand}"}</code>
              </p>
              <RichEditor value={portal.new_reply_message || ""}
                onChange={(v) => setPortal({...portal, new_reply_message: v})} minHeight={140}/>
              <div className="mt-2 flex justify-end gap-2">
                {true && (
                  <Button type="button" variant="outline" size="sm" data-testid="tmpl-default-reply"
                    onClick={() => setPortal({...portal, new_reply_message: DEFAULT_TEMPLATES.new_reply_message})}>
                    Charger le modèle par défaut
                  </Button>
                )}
                <Button type="button" variant="secondary" size="sm" data-testid="tmpl-test-reply"
                  onClick={async () => {
                    try { await api.put("/settings/portal", portal); } catch { /* noop */ }
                    try { const r = await api.post("/settings/test-email/reply"); toast.success(`Envoyé à ${r.data.sent_to}`); }
                    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  }}>
                  <EnvelopeSimple size={14} className="mr-1.5"/> Envoyer un email test
                </Button>
              </div>
            </div>

            {/* 4. Changement de statut */}
            <div className="pt-6 border-t">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-base">4. Changement de statut</Label>
                <span className="text-[0.65rem] mono px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                  {portal.notify_status_change_enabled !== false ? "activé" : "désactivé"} (voir onglet Portail)
                </span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Variables : <code>{"{client_name}"}</code>, <code>{"{ticket_number}"}</code>, <code>{"{ticket_subject}"}</code>, <code>{"{old_status}"}</code>, <code>{"{new_status}"}</code>, <code>{"{tech_name}"}</code>, <code>{"{tech_phone}"}</code>, <code>{"{brand}"}</code>
              </p>
              <RichEditor value={portal.status_change_message || ""}
                onChange={(v) => setPortal({...portal, status_change_message: v})} minHeight={140}/>
              <div className="mt-2 flex justify-end gap-2">
                {true && (
                  <Button type="button" variant="outline" size="sm" data-testid="tmpl-default-status-change"
                    onClick={() => setPortal({...portal, status_change_message: DEFAULT_TEMPLATES.status_change_message})}>
                    Charger le modèle par défaut
                  </Button>
                )}
                <Button type="button" variant="secondary" size="sm" data-testid="tmpl-test-status-change"
                  onClick={async () => {
                    try { await api.put("/settings/portal", portal); } catch { /* noop */ }
                    try { const r = await api.post("/settings/test-email/status-change"); toast.success(`Envoyé à ${r.data.sent_to}`); }
                    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  }}>
                  <EnvelopeSimple size={14} className="mr-1.5"/> Envoyer un email test
                </Button>
              </div>
            </div>

            {/* 5. Rappel avant clôture */}
            <div className="pt-6 border-t">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-base">5. Rappel avant clôture automatique</Label>
                <span className="text-[0.65rem] mono px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                  {portal.auto_close_reminder_enabled !== false ? "activé" : "désactivé"} (voir onglet Portail)
                </span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Variables : <code>{"{days_left}"}</code>, <code>{"{ticket_number}"}</code>, <code>{"{ticket_subject}"}</code>, <code>{"{client_name}"}</code>
              </p>
              <RichEditor value={portal.auto_close_reminder_message || ""}
                onChange={(v) => setPortal({...portal, auto_close_reminder_message: v})} minHeight={120}/>
              <div className="mt-2 flex justify-end gap-2">
                {true && (
                  <Button type="button" variant="outline" size="sm" data-testid="tmpl-default-reminder"
                    onClick={() => setPortal({...portal, auto_close_reminder_message: DEFAULT_TEMPLATES.auto_close_reminder_message})}>
                    Charger le modèle par défaut
                  </Button>
                )}
                <Button type="button" variant="secondary" size="sm" data-testid="tmpl-test-reminder"
                  onClick={async () => {
                    try { await api.put("/settings/portal", portal); } catch { /* noop */ }
                    try { const r = await api.post("/settings/test-email/reminder"); toast.success(`Envoyé à ${r.data.sent_to}`); }
                    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  }}>
                  <EnvelopeSimple size={14} className="mr-1.5"/> Envoyer un email test
                </Button>
              </div>
            </div>

            {/* 6. Clôture automatique */}
            <div className="pt-6 border-t">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-base">6. Clôture automatique du ticket</Label>
                <span className="text-[0.65rem] mono px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                  {portal.auto_close_enabled !== false ? "activé" : "désactivé"} (voir onglet Portail)
                </span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Variables : <code>{"{days}"}</code>, <code>{"{ticket_number}"}</code>, <code>{"{ticket_subject}"}</code>, <code>{"{client_name}"}</code>
              </p>
              <RichEditor value={portal.auto_close_message || ""}
                onChange={(v) => setPortal({...portal, auto_close_message: v})} minHeight={140}/>
              <div className="mt-2 flex justify-end gap-2">
                {true && (
                  <Button type="button" variant="outline" size="sm" data-testid="tmpl-default-auto-close"
                    onClick={() => setPortal({...portal, auto_close_message: DEFAULT_TEMPLATES.auto_close_message})}>
                    Charger le modèle par défaut
                  </Button>
                )}
                <Button type="button" variant="secondary" size="sm" data-testid="tmpl-test-auto-close"
                  onClick={async () => {
                    try { await api.put("/settings/portal", portal); } catch { /* noop */ }
                    try { const r = await api.post("/settings/test-email/auto-close"); toast.success(`Envoyé à ${r.data.sent_to}`); }
                    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  }}>
                  <EnvelopeSimple size={14} className="mr-1.5"/> Envoyer un email test
                </Button>
              </div>
            </div>

            {/* 7. Moyens de contact (broadcast) */}
            <div className="pt-6 border-t">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-base">7. Rappel des moyens de contact</Label>
                <span className="text-[0.65rem] mono px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">envoi manuel — clients sous contrat actif</span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Rappelle à vos clients en contrat de maintenance les différents canaux pour vous joindre (email, portail, téléphone, assistant IA). Utile aussi pour une communication ponctuelle vers ces clients uniquement.
                <br/>Variables : <code>{"{client_name}"}</code>, <code>{"{company}"}</code>, <code>{"{brand}"}</code>
              </p>
              <RichEditor value={portal.contact_info_message || ""}
                onChange={(v) => setPortal({...portal, contact_info_message: v})} minHeight={160}/>
              <div className="mt-2 flex justify-end gap-2 flex-wrap">
                {true && (
                  <Button type="button" variant="outline" size="sm" data-testid="tmpl-default-contact-info"
                    onClick={() => setPortal({...portal, contact_info_message: DEFAULT_TEMPLATES.contact_info_message})}>
                    Charger le modèle par défaut
                  </Button>
                )}
                <Button type="button" variant="secondary" size="sm" data-testid="tmpl-test-contact-info"
                  onClick={async () => {
                    try { await api.put("/settings/portal", portal); } catch { /* noop */ }
                    try { const r = await api.post("/settings/test-email/contact-info"); toast.success(`Envoyé à ${r.data.sent_to}`); }
                    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  }}>
                  <EnvelopeSimple size={14} className="mr-1.5"/> Envoyer un email test
                </Button>
                <Button type="button" variant="default" size="sm" data-testid="tmpl-broadcast-contact-info"
                  onClick={async () => {
                    if (!window.confirm("Envoyer ce mail à TOUS les clients ayant un contrat actif ?")) return;
                    try { await api.put("/settings/portal", portal); } catch { /* noop */ }
                    try {
                      const r = await api.post("/broadcast/contact-info");
                      toast.success(`${r.data.sent}/${r.data.total_targets || 0} client(s) contactés`);
                    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  }}>
                  <EnvelopeSimple size={14} className="mr-1.5"/> Envoyer à tous les clients sous contrat
                </Button>
              </div>
            </div>

            {/* 8. Informations d'hébergement (envoi manuel depuis Module Hébergement web) */}
            <div className="pt-6 border-t">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-base">8. Informations d'hébergement</Label>
                <span className="text-[0.65rem] mono px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300">envoi manuel depuis Hébergement web</span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Récapitulatif adressé au client depuis une fiche du module Hébergement web (bouton « Envoyer au client »).
                <br/>Variables : <code>{"{client_name}"}</code>, <code>{"{domain}"}</code>, <code>{"{plan}"}</code>, <code>{"{storage_gb}"}</code>, <code>{"{bandwidth_gb}"}</code>, <code>{"{renewal_date}"}</code>, <code>{"{webmail_url}"}</code>, <code>{"{control_panel_url}"}</code>, <code>{"{client_phone}"}</code>, <code>{"{credentials_table}"}</code>, <code>{"{brand}"}</code>
                <br/><i>Note : les mots de passe ne sont jamais inclus. Seuls les hôtes / utilisateurs / URLs d'admin apparaissent dans <code>{"{credentials_table}"}</code>.</i>
              </p>
              <RichEditor value={portal.hosting_info_message || ""}
                onChange={(v) => setPortal({...portal, hosting_info_message: v})} minHeight={200}/>
              <div className="mt-2 flex justify-end gap-2">
                <Button type="button" variant="outline" size="sm" data-testid="tmpl-default-hosting-info"
                  onClick={() => setPortal({...portal, hosting_info_message: DEFAULT_TEMPLATES.hosting_info_message})}>
                  Charger le modèle par défaut
                </Button>
                <Button type="button" variant="secondary" size="sm" data-testid="tmpl-test-hosting-info"
                  onClick={async () => {
                    try { await api.put("/settings/portal", portal); } catch { /* noop */ }
                    try { const r = await api.post("/settings/test-email/hosting-info"); toast.success(`Envoyé à ${r.data.sent_to}`); }
                    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  }}>
                  <EnvelopeSimple size={14} className="mr-1.5"/> Envoyer un email test
                </Button>
              </div>
            </div>

            {/* 9. Rappel renouvellement hébergement (cron 30j / 7j) */}
            <div className="pt-6 border-t">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-base">9. Rappel renouvellement — hébergement</Label>
                <span className="text-[0.65rem] mono px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">auto — 30 j et 7 j avant échéance</span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Envoyé automatiquement au client à J-30 et J-7 avant la date de renouvellement d'un hébergement.
                <br/>Variables : <code>{"{client_name}"}</code>, <code>{"{domain}"}</code>, <code>{"{plan}"}</code>, <code>{"{renewal_date}"}</code>, <code>{"{days_left}"}</code>, <code>{"{brand}"}</code>
              </p>
              <RichEditor value={portal.hosting_renewal_message || ""}
                onChange={(v) => setPortal({...portal, hosting_renewal_message: v})} minHeight={160}/>
              <div className="mt-2 flex justify-end gap-2">
                <Button type="button" variant="outline" size="sm" data-testid="tmpl-default-hosting-renewal"
                  onClick={() => setPortal({...portal, hosting_renewal_message: DEFAULT_TEMPLATES.hosting_renewal_message})}>
                  Charger le modèle par défaut
                </Button>
                <Button type="button" variant="secondary" size="sm" data-testid="tmpl-test-hosting-renewal"
                  onClick={async () => {
                    try { await api.put("/settings/portal", portal); } catch { /* noop */ }
                    try { const r = await api.post("/settings/test-email/hosting-renewal"); toast.success(`Envoyé à ${r.data.sent_to}`); }
                    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  }}>
                  <EnvelopeSimple size={14} className="mr-1.5"/> Envoyer un email test
                </Button>
              </div>
            </div>

            {/* 10. Rappel renouvellement nom de domaine (cron 30j / 7j) */}
            <div className="pt-6 border-t">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-base">10. Rappel renouvellement — nom de domaine</Label>
                <span className="text-[0.65rem] mono px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">auto — 30 j et 7 j avant expiration</span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Envoyé automatiquement au client à J-30 et J-7 avant l'expiration d'un nom de domaine du module Hébergement web.
                <br/>Variables : <code>{"{client_name}"}</code>, <code>{"{domain}"}</code>, <code>{"{registrar}"}</code>, <code>{"{expires_at}"}</code>, <code>{"{days_left}"}</code>, <code>{"{brand}"}</code>
              </p>
              <RichEditor value={portal.domain_renewal_message || ""}
                onChange={(v) => setPortal({...portal, domain_renewal_message: v})} minHeight={160}/>
              <div className="mt-2 flex justify-end gap-2">
                <Button type="button" variant="outline" size="sm" data-testid="tmpl-default-domain-renewal"
                  onClick={() => setPortal({...portal, domain_renewal_message: DEFAULT_TEMPLATES.domain_renewal_message})}>
                  Charger le modèle par défaut
                </Button>
                <Button type="button" variant="secondary" size="sm" data-testid="tmpl-test-domain-renewal"
                  onClick={async () => {
                    try { await api.put("/settings/portal", portal); } catch { /* noop */ }
                    try { const r = await api.post("/settings/test-email/domain-renewal"); toast.success(`Envoyé à ${r.data.sent_to}`); }
                    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  }}>
                  <EnvelopeSimple size={14} className="mr-1.5"/> Envoyer un email test
                </Button>
              </div>
            </div>

            {/* 12a. Notification — nouvelle demande de commande (module Commandes) */}
            <div className="pt-6 border-t" data-testid="tmpl-purchase-order-created">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-base">12. Notification — nouvelle demande de commande</Label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox"
                         checked={portal.purchase_order_created_enabled !== false}
                         onChange={(e) => setPortal({...portal, purchase_order_created_enabled: e.target.checked})}
                         data-testid="tmpl-purchase-order-created-enabled"/>
                  <span>Activer l'envoi</span>
                </label>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Envoyé à <b>tous les administrateurs actifs</b> dès qu'un technicien dépose une nouvelle demande dans le module <b>Commandes</b>.
                <br/>Variables : <code>{"{client_name}"}</code>, <code>{"{requester_name}"}</code>, <code>{"{invoice}"}</code>, <code>{"{requested_at}"}</code>, <code>{"{notes}"}</code>, <code>{"{products_summary}"}</code>, <code>{"{products_html}"}</code>, <code>{"{brand}"}</code>
              </p>
              <div className="mb-2">
                <Label className="text-xs">Sujet de l'email</Label>
                <input type="text" value={portal.purchase_order_created_subject || ""}
                       onChange={(e) => setPortal({...portal, purchase_order_created_subject: e.target.value})}
                       placeholder={DEFAULT_TEMPLATES.purchase_order_created_subject}
                       className="w-full rounded-md border bg-background px-3 py-1.5 text-sm mt-1"
                       data-testid="tmpl-purchase-order-created-subject"/>
              </div>
              <RichEditor value={portal.purchase_order_created_message || ""}
                onChange={(v) => setPortal({...portal, purchase_order_created_message: v})} minHeight={180}/>
              <div className="mt-2 flex justify-end gap-2">
                <Button type="button" variant="outline" size="sm" data-testid="tmpl-default-purchase-order-created"
                  onClick={() => setPortal({
                    ...portal,
                    purchase_order_created_subject: DEFAULT_TEMPLATES.purchase_order_created_subject,
                    purchase_order_created_message: DEFAULT_TEMPLATES.purchase_order_created_message,
                  })}>
                  Charger le modèle par défaut
                </Button>
                <Button type="button" variant="secondary" size="sm" data-testid="tmpl-test-purchase-order-created"
                  onClick={async () => {
                    try { await api.put("/settings/portal", portal); } catch { /* noop */ }
                    try { const r = await api.post("/settings/test-email/purchase-order-created"); toast.success(`Envoyé à ${r.data.sent_to}`); }
                    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  }}>
                  <EnvelopeSimple size={14} className="mr-1.5"/> Envoyer un email test
                </Button>
              </div>
            </div>

            {/* 13. Notification — commande passée (module Commandes) */}
            <div className="pt-6 border-t" data-testid="tmpl-purchase-order-ordered">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-base">13. Notification — commande passée</Label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox"
                         checked={portal.purchase_order_ordered_enabled !== false}
                         onChange={(e) => setPortal({...portal, purchase_order_ordered_enabled: e.target.checked})}
                         data-testid="tmpl-purchase-order-ordered-enabled"/>
                  <span>Activer l'envoi</span>
                </label>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Envoyé au <b>technicien demandeur</b> quand un admin clique « Marquer commandée » dans le module <b>Commandes</b>. 1 seul envoi par franchissement (idempotent).
                <br/>Variables : <code>{"{product_type}"}</code>, <code>{"{client_name}"}</code>, <code>{"{requester_name}"}</code>, <code>{"{ordered_by}"}</code>, <code>{"{ordered_at}"}</code>, <code>{"{tracking_info}"}</code>, <code>{"{invoice}"}</code>, <code>{"{product_url}"}</code>, <code>{"{brand}"}</code>
              </p>
              <div className="mb-2">
                <Label className="text-xs">Sujet de l'email</Label>
                <input type="text" value={portal.purchase_order_ordered_subject || ""}
                       onChange={(e) => setPortal({...portal, purchase_order_ordered_subject: e.target.value})}
                       placeholder={DEFAULT_TEMPLATES.purchase_order_ordered_subject}
                       className="w-full rounded-md border bg-background px-3 py-1.5 text-sm mt-1"
                       data-testid="tmpl-purchase-order-ordered-subject"/>
              </div>
              <RichEditor value={portal.purchase_order_ordered_message || ""}
                onChange={(v) => setPortal({...portal, purchase_order_ordered_message: v})} minHeight={180}/>
              <div className="mt-2 flex justify-end gap-2">
                <Button type="button" variant="outline" size="sm" data-testid="tmpl-default-purchase-order-ordered"
                  onClick={() => setPortal({
                    ...portal,
                    purchase_order_ordered_subject: DEFAULT_TEMPLATES.purchase_order_ordered_subject,
                    purchase_order_ordered_message: DEFAULT_TEMPLATES.purchase_order_ordered_message,
                  })}>
                  Charger le modèle par défaut
                </Button>
                <Button type="button" variant="secondary" size="sm" data-testid="tmpl-test-purchase-order-ordered"
                  onClick={async () => {
                    try { await api.put("/settings/portal", portal); } catch { /* noop */ }
                    try { const r = await api.post("/settings/test-email/purchase-order-ordered"); toast.success(`Envoyé à ${r.data.sent_to}`); }
                    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  }}>
                  <EnvelopeSimple size={14} className="mr-1.5"/> Envoyer un email test
                </Button>
              </div>
            </div>

            <div className="pt-6 border-t" data-testid="tmpl-license-low-stock">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-base">11. Alerte — stock licences bas</Label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox"
                         checked={portal.license_low_stock_enabled !== false}
                         onChange={(e) => setPortal({...portal, license_low_stock_enabled: e.target.checked})}
                         data-testid="tmpl-license-low-stock-enabled"/>
                  <span>Activer l'envoi</span>
                </label>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Envoyé aux administrateurs quand le compteur d'une fiche du module <b>Licences logiciel</b> passe sous le seuil configuré (1 seul mail par franchissement, ré-armé quand le stock remonte).
                <br/>Variables : <code>{"{title}"}</code>, <code>{"{remaining}"}</code>, <code>{"{total}"}</code>, <code>{"{used}"}</code>, <code>{"{threshold}"}</code>, <code>{"{actor}"}</code>, <code>{"{brand}"}</code>
              </p>
              <div className="mb-2">
                <Label className="text-xs">Sujet de l'email</Label>
                <input type="text" value={portal.license_low_stock_subject || ""}
                       onChange={(e) => setPortal({...portal, license_low_stock_subject: e.target.value})}
                       placeholder={DEFAULT_TEMPLATES.license_low_stock_subject}
                       className="w-full rounded-md border bg-background px-3 py-1.5 text-sm mt-1"
                       data-testid="tmpl-license-low-stock-subject"/>
              </div>
              <RichEditor value={portal.license_low_stock_message || ""}
                onChange={(v) => setPortal({...portal, license_low_stock_message: v})} minHeight={180}/>
              <div className="mt-2 flex justify-end gap-2">
                <Button type="button" variant="outline" size="sm" data-testid="tmpl-default-license-low-stock"
                  onClick={() => setPortal({
                    ...portal,
                    license_low_stock_subject: DEFAULT_TEMPLATES.license_low_stock_subject,
                    license_low_stock_message: DEFAULT_TEMPLATES.license_low_stock_message,
                  })}>
                  Charger le modèle par défaut
                </Button>
                <Button type="button" variant="secondary" size="sm" data-testid="tmpl-test-license-low-stock"
                  onClick={async () => {
                    try { await api.put("/settings/portal", portal); } catch { /* noop */ }
                    try { const r = await api.post("/settings/test-email/license-low-stock"); toast.success(`Envoyé à ${r.data.sent_to}`); }
                    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  }}>
                  <EnvelopeSimple size={14} className="mr-1.5"/> Envoyer un email test
                </Button>
              </div>
            </div>

            <div className="pt-6 border-t flex justify-end">
              <Button onClick={savePortal} data-testid="save-mail-templates"><FloppyDisk size={16} className="mr-2"/> Enregistrer tous les templates</Button>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="webhooks" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2"><PlugsConnected size={20}/> Webhook · Assistant IA</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Cet endpoint permet à un assistant virtuel externe (chatbot, IA vocale…) de créer un ticket directement dans MasterTicket, sans passer par email. La requête doit contenir le header <span className="mono">X-Webhook-Secret</span>.
          </p>

          {webhookCfg ? (
            <>
              <div>
                <Label>URL de l&apos;endpoint (POST)</Label>
                <div className="flex gap-2 mt-1">
                  <Input readOnly value={webhookCfg.url} className="mono text-xs" data-testid="webhook-url"/>
                  <Button variant="secondary" size="sm" onClick={() => { navigator.clipboard.writeText(webhookCfg.url); toast.success("URL copiée"); }} data-testid="webhook-copy-url">
                    <Copy size={14}/>
                  </Button>
                </div>
              </div>

              <div>
                <Label>Clé secrète (X-Webhook-Secret)</Label>
                <div className="flex gap-2 mt-1">
                  <Input readOnly type={secretVisible ? "text" : "password"} value={webhookCfg.secret} className="mono text-xs" data-testid="webhook-secret"/>
                  <Button variant="secondary" size="sm" onClick={() => setSecretVisible((v) => !v)} data-testid="webhook-toggle-secret">
                    {secretVisible ? "Masquer" : "Voir"}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => { navigator.clipboard.writeText(webhookCfg.secret); toast.success("Clé copiée"); }} data-testid="webhook-copy-secret">
                    <Copy size={14}/>
                  </Button>
                  <Button variant="destructive" size="sm" onClick={regenerateWebhookSecret} data-testid="webhook-regenerate">
                    <ArrowsClockwise size={14} className="mr-1.5"/> Régénérer
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground mt-1.5">
                  ⚠️ Régénérer invalide immédiatement l&apos;ancienne clé — pensez à la mettre à jour dans votre assistant.
                </div>
              </div>

              <div className="flex items-center justify-between border rounded-md p-3 bg-muted/30">
                <div>
                  <div className="text-sm font-medium">Endpoint actif</div>
                  <div className="text-xs text-muted-foreground">
                    Décochez pour bloquer temporairement toutes les créations via webhook (renvoie 403).
                  </div>
                </div>
                <Switch checked={webhookCfg.enabled} onCheckedChange={toggleWebhook} data-testid="webhook-enabled"/>
              </div>

              <div className="text-xs text-muted-foreground flex gap-6">
                <div><span className="font-medium">Total tickets créés :</span> {webhookCfg.hits || 0}</div>
                <div><span className="font-medium">Dernier appel :</span> {webhookCfg.last_used_at ? new Date(webhookCfg.last_used_at).toLocaleString("fr-FR") : "—"}</div>
              </div>

              <div>
                <Label>Exemple de payload (JSON)</Label>
                <pre className="mono text-[11px] p-3 rounded-md bg-muted/50 border overflow-x-auto mt-1" data-testid="webhook-example">{`POST ${webhookCfg.url}
Content-Type: application/json
X-Webhook-Secret: ${secretVisible ? webhookCfg.secret : "•••••••••••"}

{
  "first_name": "Jean",
  "last_name": "Dupont",
  "company": "PhoneBox",
  "email": "jean.dupont@phonebox.fr",
  "phone": "06 12 34 56 78",
  "subject": "Problème imprimante",
  "message": "Bonjour, mon imprimante ne détecte pas les cartouches.",
  "summary": "Impression bloquée",
  "priority": "urgente",
  "category": "impression",
  "source_label": "Assistant Voiceflow",
  "notify": true
}`}</pre>
                <div className="text-xs text-muted-foreground mt-2">
                  Réponse : <span className="mono">{`{ "ok": true, "ticket_id": "…", "ticket_number": N, "client_id": "…", "notified": true }`}</span>
                  <br/>Champ optionnel <span className="mono">notify: false</span> → skippe l&apos;email automatique au client (utile pour tests ou si votre assistant envoie déjà son propre message).
                  <br/>Champs alternatifs acceptés : <span className="mono">name/full_name/client_name</span>, <span className="mono">mail/client_email</span>, <span className="mono">tel/telephone/mobile</span>, <span className="mono">entreprise/société</span>, <span className="mono">résumé</span>, <span className="mono">body/content/description</span>, <span className="mono">title/topic</span>.
                </div>
              </div>
            </>
          ) : (
            <div className="text-sm text-muted-foreground">Chargement…</div>
          )}
        </CardContent>
      </Card>
      </TabsContent>

      <TabsContent value="bug-reports" className="space-y-6">
        <div data-testid="settings-bug-reports-tab" className="-mx-6 -mt-4">
          <BugReports embedded />
        </div>
      </TabsContent>

      <TabsContent value="whatsapp" className="space-y-6">
        <Card data-testid="settings-whatsapp-sender-card">
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2">
              <Phone size={20}/> Numéro WhatsApp Business
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Le numéro Twilio validé par Meta qui enverra les messages WhatsApp à vos clients.
              Format international : <code>+15553066647</code> (numéro de test) ou <code>+33939208081</code> (votre numéro français une fois validé par Meta).
              Laissez vide pour utiliser la variable d&apos;environnement <code>TWILIO_WHATSAPP_NUMBER</code>.
            </p>

            {/* v254 — Bandeau diagnostic : dernières erreurs Twilio parsées */}
            <WhatsAppDiagnosticBanner />
            <div className="max-w-md">
              <Label htmlFor="wa-sender">Numéro expéditeur</Label>
              <Input id="wa-sender" data-testid="wa-sender-number"
                type="text"
                placeholder="+15553066647"
                value={portal.whatsapp_sender_number || ""}
                onChange={(e) => setPortal({...portal, whatsapp_sender_number: e.target.value})}/>
              <div className="text-xs text-muted-foreground mt-1">
                💡 Vous devez également configurer le webhook Twilio de ce sender vers <br/>
                <code className="text-[10px] break-all">{(process.env.REACT_APP_BACKEND_URL || "") + "/api/whatsapp/webhook"}</code>
              </div>
            </div>
            <div className="max-w-md pt-4 border-t">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="wa-verify-signature-label">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={portal.whatsapp_verify_signature === true}
                  onChange={(e) => setPortal({...portal, whatsapp_verify_signature: e.target.checked})}
                  data-testid="wa-verify-signature-checkbox"
                />
                <div>
                  <div className="text-sm font-medium">Vérifier la signature Twilio des webhooks</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    <b>Désactivé par défaut</b> derrière notre reverse proxy / Cloudflare (l'URL reconstruite ne correspond pas à ce que Twilio a signé → 403). Cochez uniquement si vous exposez le webhook via un domaine direct sans proxy — sinon vos messages entrants seront bloqués.
                  </div>
                </div>
              </label>
            </div>
            <div className="flex justify-end pt-2 border-t">
              <Button onClick={savePortal} data-testid="save-whatsapp-sender">
                <FloppyDisk size={14} className="mr-1.5"/> Enregistrer le numéro
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="settings-whatsapp-tab">
          <CardHeader><CardTitle className="font-display flex items-center gap-2"><Phone size={20}/> Templates WhatsApp transactionnels</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <p className="text-sm text-muted-foreground">
              Ces messages sont envoyés automatiquement via WhatsApp au client — quand un ticket est créé, pris en charge ou résolu.
              Variables : <code>{"{prenom}"}</code>, <code>{"{numero_ticket}"}</code>, <code>{"{sujet}"}</code>, <code>{"{tech_name}"}</code>, <code>{"{societe}"}</code>, <code>{"{brand}"}</code>.
            </p>
            {[
              { key: "ack",        label: "Accusé de réception (création du ticket)" },
              { key: "taken_over", label: "Prise en charge (assignation d'un tech)" },
              { key: "resolved",   label: "Résolution (ticket marqué résolu)" },
            ].map((t) => (
              <div key={t.key} className="pt-4 border-t first:pt-0 first:border-0"
                   data-testid={`tmpl-whatsapp-${t.key}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-semibold">{t.label}</Label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setPortal({
                        ...portal,
                        [`whatsapp_${t.key}_message`]: portalDefaults.templates?.[`whatsapp_${t.key}_message`] || ""
                      })}
                      className="text-xs text-primary hover:underline"
                      data-testid={`tmpl-whatsapp-${t.key}-reset`}
                    >
                      Utiliser le défaut
                    </button>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox"
                             checked={portal[`whatsapp_${t.key}_enabled`] !== false}
                             onChange={(e) => setPortal({...portal, [`whatsapp_${t.key}_enabled`]: e.target.checked})}
                             data-testid={`tmpl-whatsapp-${t.key}-enabled`}/>
                      <span>Envoi auto activé</span>
                    </label>
                  </div>
                </div>
                <textarea
                  className="w-full min-h-[120px] rounded-md border bg-background px-3 py-2 text-sm font-mono"
                  value={portal[`whatsapp_${t.key}_message`] || ""}
                  onChange={(e) => setPortal({...portal, [`whatsapp_${t.key}_message`]: e.target.value})}
                  placeholder={portalDefaults.templates?.[`whatsapp_${t.key}_message`] || "Laissez vide pour utiliser le modèle par défaut"}
                  data-testid={`tmpl-whatsapp-${t.key}-message`}/>
              </div>
            ))}
            <div className="flex justify-end pt-4 border-t">
              <Button onClick={savePortal} data-testid="save-whatsapp-templates">
                <FloppyDisk size={14} className="mr-1.5"/> Enregistrer
              </Button>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="documentation" className="space-y-6">
        <div data-testid="settings-documentation-tab" className="-mx-6 -mt-4">
          <Documentation embedded />
        </div>
      </TabsContent>

      <TabsContent value="about" className="space-y-6">
        <AboutContent isAdmin={true} />
      </TabsContent>

      </Tabs>
    </div>
  );
}


// Wall public URL helper moved to /modules/wall (v277). If you're looking for
// the token/regenerate UI, see /app/frontend/src/pages/WallModule.jsx.


function IaNetkomAdminCard({ portal, setPortal, savePortal }) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [models, setModels] = useState([]);
  useEffect(() => {
    api.get("/ia-netkom/usage")
      .then((r) => setUsage(r.data))
      .catch(() => setUsage({ error: true }))
      .finally(() => setLoading(false));
    api.get("/ia-netkom/models").then((r) => setModels(r.data)).catch(() => setModels([]));
  }, []);
  const fmtCost = (usd) => `${(usd || 0).toFixed(4)} $`;
  const fmtNum = (n) => (n || 0).toLocaleString("fr-FR");
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2">
          <Robot size={20}/> Assistant IA
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-muted-foreground">
          Assistant IA privé accessible aux administrateurs et techniciens depuis la rubrique <span className="mono">/ia-netkom</span>. Chaque message consomme votre solde Universal Key Emergent — le coût dépend du modèle choisi ci-dessous.
        </p>

        {/* Master toggle */}
        <div className="flex items-start justify-between gap-4 p-4 rounded-lg border bg-muted/20">
          <div>
            <Label htmlFor="ia-toggle" className="font-medium cursor-pointer">Activer l&apos;Assistant IA</Label>
            <div className="text-xs text-muted-foreground mt-1">
              Désactivé : les techniciens verront un écran d&apos;information et ne pourront plus envoyer de messages. Les discussions existantes restent stockées.
            </div>
          </div>
          <Switch id="ia-toggle" data-testid="ia-netkom-toggle"
            checked={portal?.ia_netkom_enabled !== false}
            onCheckedChange={(v) => setPortal({...portal, ia_netkom_enabled: v})}/>
        </div>

        {/* Model selector */}
        <div className="p-4 rounded-lg border bg-muted/20 space-y-2">
          <Label className="font-medium">Modèle IA principal</Label>
          <div className="text-xs text-muted-foreground">
            Choisissez le compromis vitesse / qualité / coût. Haiku et Gemini Flash répondent en 1-4 s, Sonnet 4.6 est plus lent mais plus précis pour les scripts complexes.
          </div>
          <Select
            value={portal?.ia_netkom_model || "claude-sonnet-4-6"}
            onValueChange={(v) => setPortal({...portal, ia_netkom_model: v})}>
            <SelectTrigger data-testid="ia-model-select" className="mt-2">
              <SelectValue placeholder="Choisir un modèle"/>
            </SelectTrigger>
            <SelectContent>
              {models.map((m) => (
                <SelectItem key={m.id} value={m.id} data-testid={`ia-model-${m.id}`}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Per-user limits */}
        <div className="p-4 rounded-lg border bg-muted/20 space-y-3">
          <div>
            <Label className="font-medium">Plafonds par utilisateur (techniciens · admins exemptés)</Label>
            <div className="text-xs text-muted-foreground mt-1">
              La limite la plus restrictive s&apos;applique. Un technicien qui atteint un plafond reçoit un message et ne peut plus envoyer jusqu&apos;au reset (minuit local pour la limite journalière, 1er du mois pour la mensuelle).
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Messages max / jour (0 = illimité)</Label>
              <Input type="number" min={0} data-testid="ia-daily-msg-limit"
                value={portal?.ia_daily_msg_limit ?? 30}
                onChange={(e) => setPortal({...portal, ia_daily_msg_limit: parseInt(e.target.value || "0", 10)})}/>
            </div>
            <div>
              <Label className="text-xs">Coût max / mois en $ (0 = illimité)</Label>
              <Input type="number" step="0.5" min={0} data-testid="ia-monthly-cost-limit"
                value={portal?.ia_monthly_cost_limit ?? 5}
                onChange={(e) => setPortal({...portal, ia_monthly_cost_limit: parseFloat(e.target.value || "0")})}/>
            </div>
          </div>
          <div className="flex items-start justify-between gap-4 mt-2 pt-3 border-t">
            <div>
              <Label htmlFor="ia-fallback-toggle" className="font-medium cursor-pointer text-sm">Mode économique automatique (fallback Gemini Flash)</Label>
              <div className="text-xs text-muted-foreground mt-1">
                Quand un utilisateur atteint son plafond, il continue avec Gemini Flash (~40× moins cher) au lieu d&apos;être bloqué. Similaire à ChatGPT Free/Plus.
              </div>
            </div>
            <Switch id="ia-fallback-toggle" data-testid="ia-fallback-toggle"
              checked={portal?.ia_fallback_enabled !== false}
              onCheckedChange={(v) => setPortal({...portal, ia_fallback_enabled: v})}/>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={savePortal} data-testid="save-ia-netkom"><FloppyDisk size={16} className="mr-2"/> Enregistrer</Button>
        </div>

        {/* Usage stats */}
        <div className="pt-4 border-t space-y-4">
          <h3 className="font-semibold text-sm">📊 Consommation Universal Key</h3>
          {loading && <p className="text-sm text-muted-foreground">Chargement…</p>}
          {!loading && usage && !usage.error && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded-lg border">
                  <div className="text-xs uppercase text-muted-foreground">Ce mois</div>
                  <div className="text-2xl font-bold" data-testid="ia-usage-month-cost">{fmtCost(usage.this_month?.cost_usd)}</div>
                  <div className="text-xs text-muted-foreground mt-1">{fmtNum(usage.this_month?.messages)} messages</div>
                </div>
                <div className="p-3 rounded-lg border">
                  <div className="text-xs uppercase text-muted-foreground">Total historique</div>
                  <div className="text-2xl font-bold">{fmtCost(usage.total?.cost_usd)}</div>
                  <div className="text-xs text-muted-foreground mt-1">{fmtNum(usage.total?.messages)} messages</div>
                </div>
                <div className="p-3 rounded-lg border">
                  <div className="text-xs uppercase text-muted-foreground">Tokens entrants</div>
                  <div className="text-2xl font-bold mono text-blue-600">{fmtNum(usage.this_month?.input_tokens)}</div>
                  <div className="text-xs text-muted-foreground mt-1">ce mois</div>
                </div>
                <div className="p-3 rounded-lg border">
                  <div className="text-xs uppercase text-muted-foreground">Tokens sortants</div>
                  <div className="text-2xl font-bold mono text-emerald-600">{fmtNum(usage.this_month?.output_tokens)}</div>
                  <div className="text-xs text-muted-foreground mt-1">ce mois</div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-sm mb-2">Par utilisateur (30 derniers jours)</h4>
                {usage.by_user_30d?.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Aucun message envoyé sur les 30 derniers jours.</p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40">
                        <tr>
                          <th className="text-left py-2 px-3">Utilisateur</th>
                          <th className="text-right py-2 px-3">Messages</th>
                          <th className="text-right py-2 px-3">Tokens (in/out)</th>
                          <th className="text-right py-2 px-3">Coût estimé</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {usage.by_user_30d.map((u) => (
                          <tr key={u.user_id} data-testid={`ia-user-${u.user_id}`}>
                            <td className="py-2 px-3 font-medium">{u.user_name}</td>
                            <td className="py-2 px-3 text-right mono">{fmtNum(u.messages)}</td>
                            <td className="py-2 px-3 text-right mono text-xs text-muted-foreground">{fmtNum(u.input_tokens)} / {fmtNum(u.output_tokens)}</td>
                            <td className="py-2 px-3 text-right mono font-semibold">{fmtCost(u.cost_usd)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
          <p className="text-xs text-muted-foreground italic">
            💡 Les coûts sont des estimations basées sur le tarif Claude Sonnet ($3/M tokens en entrée · $15/M en sortie). Le solde Universal Key réel est visible dans votre profil Emergent (Profil → Manage plan → Universal Key).
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
